/**
 * MEV Order Job Manager
 * 
 * This utility monitors Firestore for incoming order data (when embedded in RMS POS),
 * formats it according to MEV requirements, and sends it to the MEV endpoint.
 */

import { TRANSACTION_ENDPOINT, TRANSACTION_ENDPOINT_PROD } from '../constants/requestConstant';
import { getPrivateKey } from '../../utils/certificates/getPrivateKey';
import { getIndexedDBSessionId, getFirestoreSessionDocId } from '../../utils/sessionKey';
import connectionMonitor from '../../utils/connectionMonitor';
import { getCertificate } from '../../utils/certificates/getCertificate';
import mevCertService from '../../utils/certificates/mevCertificateService';
import { readThumbprintPointer } from '../../utils/certificates/thumbprintPointer';
import certificateMemoryCache from '../../utils/certificates/certificateMemoryCache';
import mevOfflineQueue from '../../utils/mevOfflineQueue';
import { permanentUtc } from '../../utils/permanentTime';
import { logAPIResponse } from '../../utils/backgroundLogger';
import { EventEmitter } from 'events';
import { mevStorage } from './mevStorage';
import {
  CloudMevTransactionEvent,
  findEnrolledCloudMevEnv,
  getCloudMevStoreStatus,
  loadSavedCloudMevCertRecord,
  pickIssuedIdapprl,
  postPreparedCloudMevSale,
  sendCloudMevTransaction,
  serializeCloudMevError,
} from '../../cloud-mev/api/cloudMevApi';
import { resolveCloudMevEnv } from '../../cloud-mev/cloudMevEnv';
import { caseForEssaiHeader } from '../../cloud-mev/essaiTestCase';
import { logExceptionScenario, logExceptionScenarios } from './exceptionScenario';
import { resolveFormImpr } from './receiptFormImpr';
import { ensureRqProduct } from './rqProduct';
import { logSrmExchange, srmErrorText } from './srmApiLog';
import { commerElectrFor, onlineModPai, onlineTypServ } from './onlineFiscal';
import { clintFromParty, loadFrequentThirdParty } from './frequentThirdParty';
import masterDataCache from '../../utils/offlineStorage/masterDataCache';
import { lineTaxCode, refundFromLines } from '../../utils/tax/itemTax';
import { lineItemActi, storeBillingSubactivity } from '../../components/constants/BillingActivity';
import { readStoreLocal } from '../../utils/storeScope';

/**
 * Get API wait time from settings (default: 5 seconds)
 * Returns timeout in milliseconds
 */
function getApiWaitTime(): number {
  try {
    const settingsEnvrn = readStoreLocal('Settings_envrn');
    if (settingsEnvrn) {
      const parsed = JSON.parse(settingsEnvrn) as { apiWaitTime?: number };
      const waitTimeSeconds = parsed.apiWaitTime || 5;
      // Convert seconds to milliseconds and ensure minimum of 5 seconds
      return Math.max(5000, waitTimeSeconds * 1000);
    }
  } catch (error) {
    console.error('[MEV] Error reading API wait time from settings:', error);
  }
  // Default: 5 seconds = 5000 milliseconds
  return 5000;
}

interface OrderJob {
  id: string;
  data: any;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  createdAt: Date;
  processedAt?: Date;
  result?: any;
  error?: string;
  formattedData?: any;
  billType?: 'temporary' | 'final' | 'cancellation'; // Bill type for RMS-POS: temporary (ADDI), final (RFER), or cancellation (SOB)
  modImpr?: string; // FAC, DUP, ANN, etc.
}

// Define a type for the polling status change callback
type PollingStatusCallback = (isPolling: boolean) => void;

interface TaxDetail {
  label: string;
  total: number;
  fiscalType?: string;
}

interface OrderItem {
  qty: number;
  name: string;
  price: number;
  sku?: string;
  id?: string;
  tax?: string;
  preci?: Array<{ descr: string; acti: string }>;
  special_instructions?: string;
  fiscalActivity?: string;
  thirdPartyId?: string;
  seatNumber?: number;
  taxIds?: string[];
}

const ITEM_FISCAL_ACTIVITIES = ['RES', 'BAR', 'CDR', 'NON', 'HAB'];

const LINE_TAX_CODES = ['F', 'P', 'S', 'FP', 'FS', 'PS', 'FPS', 'NON'];

function codeForLine(item: any, storeTaxes: any[], onAlcoholList: boolean): string {
  if (Array.isArray(item?.taxIds) && storeTaxes.length > 0) {
    return lineTaxCode(item, { taxes: storeTaxes });
  }
  const explicit = String(item?.tax || '').toUpperCase();
  if (LINE_TAX_CODES.includes(explicit)) return explicit;
  return onAlcoholList ? 'FPS' : 'FP';
}

function itemFiscalActivity(item: any): string {
  const value = item?.fiscalActivity;
  return ITEM_FISCAL_ACTIVITIES.includes(value) ? value : '';
}

function copyFiscalTags(item: any): { fiscalActivity?: string; thirdPartyId?: string } {
  const fiscalActivity = itemFiscalActivity(item);
  const thirdPartyId = fiscalActivity === 'HAB' ? String(item?.thirdPartyId || '') : '';
  return {
    ...(fiscalActivity ? { fiscalActivity } : {}),
    ...(thirdPartyId ? { thirdPartyId } : {}),
  };
}

interface Order {
  order_id: string;
  tax_details?: TaxDetail[];
  items?: OrderItem[];
  sub_total: number;
  tax_amount: number;
  total: number;
  payment_method?: {
    code: string;
  };
  note?: string;
  special_instructions?: string;
  refunds?: any[];
  order_channel?: string;
  table_name?: string;
  table_id?: string;
  guest_count?: {
    adult?: number;
    kid?: number;
    senior?: number;
    other?: number;
    total?: number;
  };
  session_id?: string;
  seat_number?: number | null;
}



interface OrderData {
  order?: Order;
  note?: string;
  session_id?: string;
  user_name?: string;
  auditLog?: any[];
  printHistory?: any[];
  printVersionSuffix?: number;
  isRevised?: boolean;
  replacesCount?: number;
  tipAmount?: number;
  modImpr?: string;
  refs?: Array<{
    noTrans: string;
    datTrans: string;
    avantTax: string;
  }>;
  storeTaxes?: any[];  // Store's tax configuration for alcohol detection
  isCorrected?: boolean; // Flag to indicate corrected receipt (for receipt rendering)
  isReverse?: boolean; // Flag to indicate reverse transaction (for formImpr: NON)
  formImpr?: string;
  billAdjustment?: number;
  receiptDelivery?: string | null;
  receiptEmail?: string | null;
  merchantCopy?: boolean;
  sourceChannel?: string;
  serviceType?: string;
  onlineOrderChannel?: string;
  platform?: string;
  paymentStatus?: string;
  paymentMethodRaw?: any;
  paymentMethodCode?: string | null;
  onlineCheckoutMethod?: string | null;
  onlineCheckoutLabel?: string | null;
  isPaid?: boolean;
}

// Helper function to determine service type (typServ) based on order channel and order ID
function channelReferenceNumber(currentNoTrans: string, recorded?: string): string {
  const channelNumber = /^(?:CS|TW|DL)F?\d+(?:-\d+)?$/i;
  const recordedText = String(recorded || '').trim();
  if (channelNumber.test(recordedText)) return recordedText;
  const current = String(currentNoTrans || '').trim();
  const match = current.match(/^((?:CS|TW|DL)F?\d+)(?:-(\d+))?$/i);
  if (!match) return '';
  const revision = Number(match[2] || 0);
  if (revision <= 1) return match[1];
  return `${match[1]}-${revision - 1}`;
}

function priorBillHistoryEntry(history: any[] | undefined, currentNoTrans: string) {
  const current = String(currentNoTrans || '').trim();
  return [...(history || [])].reverse().find((entry) => {
    const noTrans = String(entry?.transactionNumber || '').trim();
    if (!noTrans || noTrans === current) return false;
    const type = String(entry?.type || '');
    return type !== 'cancellation' && type !== 'REFUND' && type !== 'abandonment';
  }) || null;
}

function determineServiceType(orderData: OrderData): string {
  const onlineService = onlineTypServ(orderData);
  if (onlineService) return onlineService;
  const channel = orderData.order?.order_channel?.toUpperCase() || '';
  const orderId = orderData.order?.order_id || '';
  console.log('MEV orderData.', orderData);
  // First try to determine from order channel
  if (channel === 'TABLE') return 'TBL';
  if (channel === 'DELIVERY' || channel === 'LOCAL' || channel === 'LOCAL DELIVERY') return 'LVR';
  if (channel === 'COUNTER' || channel === 'TAKEAWAY') return 'CMP';

  // Fallback: Determine from order ID prefix
  // CS = Counter Service, TW = Takeaway, DL = Delivery, T = Table (e.g., T23001)
  if (orderId.startsWith('CS')) return 'CMP';
  if (orderId.startsWith('TW')) return 'CMP';
  if (orderId.startsWith('DL')) return 'LVR';
  if (orderId.startsWith('T') && orderId.match(/^T\d+/)) return 'TBL'; // Table IDs like T23001

  return 'CMP'; // Default to counter service
}

// Helper function to format table number (noTabl) - only for table service
function getTableNumber(orderData: OrderData, serviceType: string): string | undefined {
  // Only show table number for table service (TBL)
  if (serviceType !== 'TBL') {
    return undefined;
  }

  const tableName = orderData.order?.table_name;
  if (!tableName) return undefined;

  // Use full table name (alphanumeric) and pad LEFT with '=' to 5 characters
  // Examples: "VP2" -> "==VP2", "VIP" -> "==VIP", "40" -> "===40", "A1" -> "===A1"
  const tableNameStr = String(tableName).trim();
  return tableNameStr.padStart(5, '=');
}

// Helper function to get number of customers (nbClint)
function getClientNumber(orderData: OrderData): string {
  const seats = new Set<number>();
  (orderData.order?.items || []).forEach((item) => {
    const seat = Number(item?.seatNumber);
    if (seat > 0 && Number(item?.qty) > 0) seats.add(seat);
  });
  const total = seats.size > 0 ? seats.size : 1;

  // Format: 3 digits, left-padded with '0'
  return String(total).padStart(3, '0');
}

/**
 * Helper function to check if user is in training mode
 * @param {string | null} userId - POS user ID
 * @returns {boolean} True if user is in training mode
 */
function isUserInTrainingMode(userId: string | null): boolean {
  if (!userId) return false;

  try {
    const storageKey = `trainingMode_${userId}`;
    const stored = localStorage.getItem(storageKey);

    if (!stored) return false;

    const state = JSON.parse(stored);
    if (!state.isTrainingMode || !state.startTime) return false;

    // Check if timer has expired (90 minutes = 5400 seconds)
    const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
    if (elapsed >= 5400) {
      // Timer expired - clear state
      localStorage.removeItem(storageKey);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error checking training mode:', error);
    return false;
  }
}

function chosenMerchantCopy(source: any): boolean | undefined {
  if (typeof source?.merchantCopy === 'boolean') return source.merchantCopy;
  if (typeof source?.order?.merchantCopy === 'boolean') return source.order.merchantCopy;
  if (typeof source?.originalOrder?.merchantCopy === 'boolean') return source.originalOrder.merchantCopy;
  return undefined;
}

function merchantCopyForPayload(transActu: any, responseData: any, override?: boolean): { enabled: boolean; data: any } {
  const modImpr = transActu?.modImpr;
  if (!transActu || transActu.formImpr === 'NON' || modImpr === 'ANN') {
    return { enabled: false, data: null };
  }
  const isAuc = transActu.modPai === 'AUC' && modImpr === 'PSP';
  let settings: { merchantCopy?: boolean; merchantCopyTemporary?: boolean; merchantCopyClosing?: boolean } = {};
  try {
    settings = JSON.parse(readStoreLocal('Settings_envrn') || '{}');
  } catch {
    settings = {};
  }
  const enabled = isAuc
    ? true
    : typeof override === 'boolean'
      ? override
      : transActu.typTrans === 'ADDI'
        ? settings.merchantCopyTemporary === true || (settings.merchantCopyTemporary === undefined && settings.merchantCopy === true)
        : settings.merchantCopyClosing === true;
  if (!enabled) return { enabled: false, data: null };
  return {
    enabled: true,
    data: {
      reqTrans: {
        ...responseData.reqTrans,
        transActu: { ...transActu, modImpr: isAuc ? 'PSP' : 'DUP' },
      },
      retourTrans: responseData.retourTrans,
    },
  };
}

class MevOrderJobManager {
  private static instance: MevOrderJobManager;
  private jobs: OrderJob[] = [];
  private eventListeners: { [key: string]: Function[] } = {};
  private cloudEnrollCache: { key: string; enrolled: boolean; at: number; envirn?: string } | null = null;
  private readonly MAX_JOBS = 200;
  private readonly MEV_PAYMENT_METHODS = [
    'CRE', 'DEB', 'TFD', 'CPR', 'CHQ', 'COU', 'MIX', 'AUC', 'AUT', 'ARG', 'SOB', 'MVO', 'PAC'
  ] as const;
  private firestoreMonitoring: boolean = false;

  private constructor() {
    // Firestore monitoring is always enabled in RMS-POS
    this.firestoreMonitoring = true;
  }

  /**
   * Get the singleton instance of the MevOrderJobManager
   */
  public static getInstance(): MevOrderJobManager {
    if (!MevOrderJobManager.instance) {
      MevOrderJobManager.instance = new MevOrderJobManager();
    }
    return MevOrderJobManager.instance;
  }

  /**
   * Start monitoring (Firestore mode only)
   */
  public startPolling(): void {
    this.firestoreMonitoring = true;
    console.log('MEV: Firestore monitoring enabled');
  }

  /**
   * Enable Firestore monitoring mode
   */
  public enableFirestoreMonitoring(): void {
    this.firestoreMonitoring = true;
    console.log('MEV: Firestore monitoring enabled');
  }

  /**
   * Get the current monitoring status
   */
  public getIsPolling(): boolean {
    return this.firestoreMonitoring;
  }

  /**
   * Stop monitoring (no-op in Firestore mode)
   */
  public stopPolling(): void {
    console.log('MEV: Firestore monitoring cannot be stopped');
  }

  /**
   * Local RQ send is retired. Cloud Functions POST to Revenu Québec.
   */
  public async handleOrderFromFirestore(order: any, orderId: string): Promise<any> {
    if (order?.cloudMev?.rqResponse || order?.cloudMev?.rqRequest) {
      await this.handleCloudMevResponse(order, orderId);
      return;
    }
    console.log(`[MEV] Skipping local send for ${orderId} — Cloud Functions send to Revenu Québec`);
  }

  public async handleCloudMevResponse(order: any, orderId?: string): Promise<void> {
    const resolvedId = orderId || order?.id;
    const eventId = order?.mevEventId || order?.cloudMev?.eventId || 'response';
    const jobId = `cloud-${resolvedId}-${eventId}`;
    console.log('Back to station', 'handle response', { orderId: resolvedId, eventId, jobId });
    if (this.jobs.some((job) => job.id === jobId)) {
      console.log('Back to station', 'skip duplicate job', { orderId: resolvedId, eventId, jobId });
      console.log('[Cloud MEV Print] skip duplicate job', { orderId: resolvedId, eventId, jobId });
      return;
    }

    const rqRequest = order?.cloudMev?.rqRequest;
    const rqResponse = order?.cloudMev?.rqResponse;
    if (!rqRequest && !rqResponse) {
      console.log('Back to station', 'skip print, no rq body', { orderId: resolvedId, eventId });
      return;
    }

    const responseData = {
      ...(rqRequest || {}),
      ...(typeof rqResponse === 'object' && rqResponse ? rqResponse : {}),
      retourTrans: rqResponse?.retourTrans,
    };

    const qrCodeUrl = String(order?.cloudMev?.qrCodeUrl || '');
    const transActu = rqRequest?.reqTrans?.transActu || {};
    const merchantCopy = merchantCopyForPayload(transActu, responseData, chosenMerchantCopy(order));
    console.log('[Cloud MEV Print] handling return', {
      orderId: resolvedId,
      eventId,
      jobId,
      qrCodeUrl,
      formImpr: rqRequest?.reqTrans?.transActu?.formImpr,
      typTrans: rqRequest?.reqTrans?.transActu?.typTrans,
      noTrans: rqRequest?.reqTrans?.transActu?.noTrans,
      psiNoTrans: order?.psiNoTrans || rqResponse?.retourTrans?.retourTransActu?.psiNoTrans,
    });

    const event = String(order?.mevEvent || order?.cloudMev?.event || 'final');
    const job: OrderJob = {
      id: jobId,
      data: { order, originalOrder: order },
      status: 'completed',
      createdAt: new Date(),
      processedAt: new Date(),
      billType: event === 'temporary' ? 'temporary' : event === 'cancel_before_print' ? 'cancellation' : 'final',
      result: {
        cloud: true,
        responseData,
        qrCodeUrl,
        qrCodeSize: 440,
        originalOrderData: {
          ...order,
          modImpr: transActu.modImpr,
          isCorrected: Boolean(order?.isCorrected) || event === 'correct_payment_step2',
        },
        psiNoTrans: order?.psiNoTrans || rqResponse?.retourTrans?.retourTransActu?.psiNoTrans,
        merchantCopyEnabled: merchantCopy.enabled,
        merchantResponseData: merchantCopy.data,
      },
    };
    this.jobs.push(job);
    if (this.jobs.length > this.MAX_JOBS) this.jobs = this.jobs.slice(-this.MAX_JOBS);
    console.log('Back to station', 'notify printer', {
      jobId,
      billType: job.billType,
      listenerCount: this.eventListeners['job-processed']?.length || 0,
      hasQr: Boolean(qrCodeUrl),
    });
    console.log('[Cloud MEV Print] job-processed', { jobId, billType: job.billType, qrCodeUrl, merchantCopy: merchantCopy.enabled });
    const listErr = rqResponse?.retourTrans?.retourTransActu?.listErr;
    const hasErrors = Array.isArray(listErr) && listErr.length > 0;
    logAPIResponse(
      order?.cloudMev?.rqUrl || 'WEB-SRM transaction',
      'POST',
      rqRequest || {},
      rqResponse || {},
      0,
      hasErrors ? 400 : 200
    ).catch((error) => {
      console.error('[Cloud MEV Print] Failed to write API log:', error);
    });
    this.notifyListeners('job-processed', job);
  }

  private channelRoot(order: any): string {
    const channelUpper = String(order?.orderChannel || '').toUpperCase();
    if (channelUpper === 'TABLE' || channelUpper === 'TBL') {
      return String(order?.sessionId || order?.tableName || order?.orderNumber || '').trim();
    }
    const orderNumber = String(order?.orderNumber || '').trim();
    if (/^(CS|TW|DL)(?:[B-D])?(?:Z|F)?\d+$/i.test(orderNumber)) return orderNumber;
    const online = channelUpper === 'ONLINE'
      || String(order?.channel || '').toUpperCase() === 'ONLINE'
      || String(order?.sessionId || '').startsWith('anon_');
    if (online) {
      const count = String(order?.channelOrderNumber || '').replace(/^#/, '').trim()
        || (order?.counter != null ? String(order.counter).trim() : '');
      if (count) return count;
    }
    return String(order?.tableName || order?.sessionId || orderNumber || '').trim();
  }

  private asDate(value: any): Date {
    if (value?.toDate) return value.toDate();
    const date = value instanceof Date ? value : new Date(value || Date.now());
    return Number.isNaN(date.getTime()) ? new Date() : date;
  }

  /** The noTrans already issued for this order, before the next suffix is taken. */
  private currentBillRef(order: any): { noTrans: string; datTrans: string; avantTax: string } | null {
    const { generateTransactionNumber } = require('../../utils/auditLogger');
    const { tableDocumentNumber } = require('../../utils/orderNumberGenerator');
    const root = this.channelRoot(order);
    if (!root) return null;
    const suffix = Number(order?.printVersionSuffix || 0);
    const channelUpper = String(order?.orderChannel || '').toUpperCase();
    const noTrans = (channelUpper === 'TABLE' || channelUpper === 'TBL')
      ? tableDocumentNumber(root, suffix)
      : generateTransactionNumber(root, suffix);
    const history = Array.isArray(order?.printHistory) ? order.printHistory : [];
    const match = [...history].reverse().find((entry: any) => entry?.transactionNumber === noTrans)
      || [...history].reverse().find((entry: any) => entry?.transactionNumber && entry?.type !== 'cancellation' && entry?.type !== 'REFUND');
    const storedDatTrans = String(match?.datTrans || '');
    const datTrans = /^\d{14}$/.test(storedDatTrans)
      ? storedDatTrans
      : this.formatDateTime(this.asDate(match?.timestamp || order?.paidAt || order?.billPrintedAt || order?.created));
    const avantTax = Number(order?.financials?.subtotal ?? order?.subtotal ?? order?.total ?? 0);
    return { noTrans, datTrans, avantTax: this.formatAmount(avantTax) };
  }

  private acceptedPriorRef(order: any): { noTrans: string; datTrans: string; avantTax: string } | null {
    const history = Array.isArray(order?.printHistory) ? order.printHistory : [];
    const prior = [...history].reverse().find((entry: any) => entry?.transactionNumber && entry?.type !== 'cancellation' && entry?.type !== 'REFUND');
    const noTrans = String(prior?.transactionNumber || '').trim();
    if (!noTrans) return null;
    const rawDate = prior?.datTrans || prior?.timestamp || order?.created || Date.now();
    const datTrans = /^\d{14}$/.test(String(prior?.datTrans || ''))
      ? String(prior.datTrans)
      : this.formatDateTime(new Date(rawDate));
    const avantTax = Number(prior?.avantTax ?? order?.originalSubtotal ?? order?.financials?.subtotal ?? order?.subtotal ?? 0);
    return { noTrans, datTrans, avantTax: this.formatAmount(avantTax) };
  }

  private priorBillRef(order: any): { noTrans: string; datTrans: string; avantTax: string } | null {
    const history = Array.isArray(order?.printHistory) ? order.printHistory : [];
    const prior = [...history].reverse().find((entry: any) => entry?.transactionNumber && entry?.type !== 'cancellation' && entry?.type !== 'REFUND');
    const noTrans = String(prior?.transactionNumber || order?.orderNumber || order?.tableName || '').trim();
    if (!noTrans) return null;
    const rawDate = prior?.datTrans || prior?.timestamp || order?.created || Date.now();
    const datTrans = /^\d{14}$/.test(String(prior?.datTrans || ''))
      ? String(prior.datTrans)
      : this.formatDateTime(new Date(rawDate));
    const avantTax = Number(prior?.avantTax ?? order?.originalSubtotal ?? order?.financials?.subtotal ?? order?.subtotal ?? 0);
    return { noTrans, datTrans, avantTax: this.formatAmount(avantTax) };
  }

  private paymentCodeFromOrder(order: any): string {
    const explicit = String(order?.paymentMethodCode || order?.paymentMethod?.jsonCode || order?.paymentMethod?.code || '').toUpperCase();
    if ((this.MEV_PAYMENT_METHODS as readonly string[]).includes(explicit)) return explicit;
    return '';
  }

  private itemTaxCode(order: any, item: any): string {
    const taxes = Array.isArray(order?.storeTaxes) ? order.storeTaxes : [];
    const alcohol = taxes.find((tax: any) => {
      const name = String(tax?.taxName || '').toLowerCase();
      return name === 'quebec-alcohol' || name === 'tax s';
    });
    const ids = new Set((alcohol?.checkedItems || []).map((entry: any) => entry?.id).filter(Boolean));
    const key = item?.sku || item?.id;
    return codeForLine(item, taxes, Boolean(key && ids.has(key)));
  }

  private shapeCancelAfterPrint(order: any): any {
    const sourceItems = (Array.isArray(order?.items) ? order.items : [])
      .filter((item: any) => item?.sku !== 'ANNULATION' && item?.name !== 'Annulation');
    const sums: Record<string, number> = {};
    sourceItems.forEach((item: any) => {
      const qty = Number(item?.qty || item?.amount || 1);
      const code = this.itemTaxCode(order, item);
      sums[code] = (sums[code] || 0) + (Number(item?.price) || 0) * qty;
    });
    const annulation = LINE_TAX_CODES
      .map((code) => {
        const amount = Math.round((sums[code] || 0) * 100) / 100;
        if (amount === 0) return null;
        return {
          qty: 1,
          amount: 1,
          name: 'Annulation',
          price: -Math.abs(amount),
          sku: 'ANNULATION',
          id: 'ANNULATION',
          tax: code,
          preci: [{ descr: 'commande annulée', acti: 'SOB' }],
        };
      })
      .filter(Boolean);
    if (annulation.length === 0) {
      annulation.push({
        qty: 1,
        amount: 1,
        name: 'Annulation',
        price: 0,
        sku: 'ANNULATION',
        id: 'ANNULATION',
        tax: 'FP',
        preci: [{ descr: 'commande annulée', acti: 'SOB' }],
      });
    }
    const ref = this.acceptedPriorRef(order);
    return {
      ...order,
      items: [
        ...sourceItems.map((item: any) => ({ ...item, itemDiscount: 0 })),
        ...annulation,
      ],
      taxes: [],
      subtotal: 0,
      total: 0,
      totalWithTaxes: 0,
      financials: { subtotal: 0, taxTotal: 0, grandTotal: 0 },
      paymentMethod: { code: 'AUC' },
      paymentMethodCode: 'AUC',
      modImpr: 'FAC',
      refs: ref ? [ref] : [],
    };
  }

  private shapeCreditNote(order: any): any {
    const custom = Number(order?.mevRefundAmount) > 0 ? Number(order.mevRefundAmount) : 0;
    const tpsRate = order?.taxes?.find((tax: any) => String(tax?.taxName || '').toUpperCase() === 'TPS')?.taxRate || 0.05;
    const tvqRate = order?.taxes?.find((tax: any) => String(tax?.taxName || '').toUpperCase() === 'TVQ')?.taxRate || 0.09975;
    const selected = Array.isArray(order?.mevRefundItems) ? order.mevRefundItems : [];
    let refundSubtotal = custom > 0
      ? custom / (1 + tpsRate + tvqRate)
      : selected.reduce((sum: number, item: any) => sum + (Number(item?.price) || 0) * Number(item?.quantity || item?.qty || item?.amount || 1), 0);
    refundSubtotal = Math.round(refundSubtotal * 100) / 100;
    const refundTPS = Math.round(refundSubtotal * tpsRate * 100) / 100;
    const refundTVQ = Math.round(refundSubtotal * tvqRate * 100) / 100;
    const refundTotal = Math.round((refundSubtotal + refundTPS + refundTVQ) * 100) / 100;
    const ref = this.currentBillRef(order);
    const paymentCode = this.paymentCodeFromOrder(order) || 'ARG';
    const nextSuffix = Number(order?.printVersionSuffix || 0) + 1;
    const items = custom > 0
      ? [{ qty: 1, amount: 1, name: 'Remboursement partiel', price: -refundSubtotal, sku: 'CUSTOM_REFUND', id: 'CUSTOM_REFUND' }]
      : selected.map((item: any) => ({
        ...item,
        qty: item.quantity || item.qty || item.amount || 1,
        amount: item.quantity || item.qty || item.amount || 1,
        price: -(Number(item.price) || 0),
        itemDiscount: 0,
      }));
    return {
      ...order,
      items,
      taxes: [
        { taxName: 'TPS', taxAmount: -refundTPS },
        { taxName: 'TVQ', taxAmount: -refundTVQ },
      ],
      subtotal: -refundSubtotal,
      total: -refundSubtotal,
      totalWithTaxes: -refundTotal,
      financials: { subtotal: -refundSubtotal, taxTotal: -(refundTPS + refundTVQ), grandTotal: -refundTotal },
      paymentMethod: { code: paymentCode },
      paymentMethodCode: paymentCode,
      modImpr: 'FAC',
      printVersionSuffix: nextSuffix,
      refs: ref ? [ref] : (order?.refs || []),
    };
  }

  private shapePaymentReverse(order: any): any {
    const items = (order?.items || []).map((item: any) => ({
      ...item,
      price: -(Number(item?.price) || 0),
      itemDiscount: 0,
    }));
    const subtotal = Number(order?.financials?.subtotal || order?.subtotal || 0);
    const taxTotal = Number(order?.financials?.taxTotal || 0);
    const grandTotal = Number(order?.financials?.grandTotal || order?.totalWithTaxes || order?.total || 0);
    const ref = this.currentBillRef(order);
    const nextSuffix = Number(order?.printVersionSuffix || 0) + 1;
    return {
      ...order,
      items,
      taxes: (order?.taxes || []).map((tax: any) => ({ ...tax, taxAmount: -(Number(tax?.taxAmount) || 0) })),
      subtotal: -subtotal,
      total: -subtotal,
      totalWithTaxes: -grandTotal,
      financials: { subtotal: -subtotal, taxTotal: -taxTotal, grandTotal: -grandTotal },
      paymentMethod: { code: 'AUC' },
      paymentMethodCode: 'AUC',
      modImpr: 'FAC',
      isReverse: true,
      printVersionSuffix: nextSuffix,
      refs: ref ? [ref] : (order?.refs || []),
    };
  }

  private shapePaymentCorrected(order: any): any {
    const paymentCode = String(order?.mevNewPaymentMethodCode || '').toUpperCase() || this.paymentCodeFromOrder(order) || 'ARG';
    const reverse = order?.mevReverseRef;
    const nextSuffix = Number(order?.printVersionSuffix || 0) + 2;
    return {
      ...order,
      paymentMethod: { code: paymentCode },
      paymentMethodCode: paymentCode,
      modImpr: 'FAC',
      isReverse: false,
      isCorrected: true,
      printVersionSuffix: nextSuffix,
      refs: reverse?.noTrans ? [reverse] : (order?.refs || []),
    };
  }

  private async orderWithStoreTaxes(order: any): Promise<any> {
    if (Array.isArray(order?.storeTaxes) && order.storeTaxes.length > 0) return order;
    const storeId = String(order?.storeId || mevStorage.getStoreId() || localStorage.getItem('selectedStoreId') || '').trim();
    if (!storeId) return order;
    try {
      const store = await masterDataCache.get('stores', storeId);
      if (Array.isArray(store?.taxes) && store.taxes.length > 0) {
        return { ...order, storeTaxes: store.taxes };
      }
    } catch (error) {
      console.error('[MEV ALCOHOL TAX] Could not read store taxes', error);
    }
    return order;
  }

  /**
   * Build the Revenue Québec reqTrans on this station, then print it.
   * Offline bills use this same object later as transLot. The footer is
   * PROBLÈME DE COMMUNICATION until Revenue Québec returns psi fields.
   */
  public async formatAndPrintBill(order: any, event: string, offline = true, printReceipt = true): Promise<any> {
    const source = await this.orderWithStoreTaxes(order || {});
    let working = source;
    if (event === 'cancel_before_print') {
      working = {
        ...source,
        modImpr: 'ANN',
        items: [],
        taxes: [],
        subtotal: 0,
        total: 0,
        totalWithTaxes: 0,
        financials: { subtotal: 0, taxTotal: 0, grandTotal: 0 },
      };
    } else if (event === 'cancel_after_print') working = this.shapeCancelAfterPrint(source);
    else if (event === 'refund') working = this.shapeCreditNote(source);
    else if (event === 'correct_payment_step1') working = this.shapePaymentReverse(source);
    else if (event === 'correct_payment_step2') working = this.shapePaymentCorrected(source);
    if (event === 'kitchen') {
      const allItems = working.items || [];
      const activeItems = allItems.filter((item: any) => !item?.isOnHold);
      let kitchenOrder = {
        ...working,
        items: activeItems,
        formImpr: 'NON',
        modImpr: 'FAC',
        paymentMethodCode: 'SOB',
      };
      if (activeItems.length !== allItems.length) {
        const held = allItems.filter((item: any) => item?.isOnHold).reduce((sum: number, item: any) => {
          const qty = Number(item?.amount || item?.qty || 1);
          const unit = Number(item?.totalPrice ?? item?.price ?? 0);
          return sum + (unit * qty);
        }, 0);
        const subtotal = Math.max(0, Number(working.financials?.subtotal ?? working.subtotal ?? working.total ?? 0) - held);
        const taxes = (working.taxes || []).map((tax: any) => {
          const rate = Number(tax?.taxValue ?? tax?.rate ?? 0);
          return { ...tax, taxAmount: Math.round(subtotal * (rate / 100) * 100) / 100 };
        });
        const taxTotal = taxes.reduce((sum: number, tax: any) => sum + Number(tax.taxAmount || 0), 0);
        const grandTotal = Math.round((subtotal + taxTotal) * 100) / 100;
        kitchenOrder = {
          ...kitchenOrder,
          subtotal,
          total: subtotal,
          totalWithTaxes: grandTotal,
          taxes,
          financials: { ...(working.financials || {}), subtotal, taxTotal, grandTotal },
        };
      }
      working = kitchenOrder;
    }
    if ((event === 'temporary' || event === 'final') && working.formImpr !== 'NON') {
      const seat = Number(working.seatNumber) > 0 ? Number(working.seatNumber) : 0;
      const explicit = Array.isArray(working.receiptItems) ? working.receiptItems : [];
      const saved = Array.isArray(working.items) ? working.items : [];
      const billed = explicit.length > 0
        ? explicit
        : (seat > 0 ? saved.filter((item: any) => Number(item.seatNumber || 0) === seat) : saved);
      working = { ...working, items: billed };
    }
    const billType = (event === 'temporary' || event === 'kitchen')
      ? 'temporary'
      : (event === 'cancel_before_print' ? 'cancellation' : 'final');
    console.log('internet disconnected debug', 'format start', {
      event,
      billType,
      offline,
      orderId: order?.id,
      orderNumber: order?.orderNumber,
      itemCount: order?.items?.length || 0,
    });
    const orderData = this.convertRmsPosOrderToMev(working, working?.id || working?.orderNumber || 'bill');
    if (working?.modImpr) orderData.modImpr = working.modImpr;
    if (working?.isReverse) orderData.isReverse = true;
    if (working?.isCorrected) orderData.isCorrected = true;
    console.log('internet disconnected debug', 'converted order for formatter', orderData);
    const formatStarted = Date.now();
    const formattedData = await this.formatOrderData(orderData, billType, working?.modImpr);
    const transActu = formattedData?.reqTrans?.transActu || {};
    const envirn = mevStorage.getEnvirn() || 'DEV';
    const storeId = mevStorage.getStoreId() || '';
    const identityReady = Boolean(
      transActu.noDossFO
      && transActu.noTax?.noTPS
      && transActu.noTax?.noTVQ
      && transActu.SEV?.idSEV
      && transActu.SEV?.idVersi
      && transActu.SEV?.idPartn
    );
    if (transActu.noTrans && storeId && !identityReady) {
      const status = await getCloudMevStoreStatus(storeId, envirn).catch(() => null);
      const cloud = status?.snapshot?.transHeader || {};
      const csr = status?.snapshot?.csrForm || {};
      const pick = (...values: unknown[]) => String(values.find((value) => String(value || '').trim()) || '');
      transActu.noDossFO = pick(transActu.noDossFO, cloud.noDossFO, csr.dossierNumber);
      transActu.noTax = {
        noTPS: pick(transActu.noTax?.noTPS, cloud.noTPS, csr.gstNumber),
        noTVQ: pick(transActu.noTax?.noTVQ, cloud.noTVQ, csr.qstNumber),
      };
      const product = await ensureRqProduct(storeId);
      transActu.SEV = {
        idSEV: product.IDSEV,
        idVersi: product.IDVERSI,
        codCertif: product.CODCERTIF,
        idPartn: product.IDPARTN,
        versi: product.VERSI,
        versiParn: product.VERSIPARN,
      };
    }
    console.log('[MEV timing] format', {
      noTrans: transActu.noTrans || '',
      ms: Date.now() - formatStarted,
      identityReady,
      items: transActu.items?.length || 0,
    });
    let qrCodeUrl = '';
    if (transActu.noTrans) {
      const signatureData = await this.generateTransactionSignature({
        noTrans: transActu.noTrans,
        datTrans: transActu.datTrans,
        tpsAmount: transActu.mont?.TPS,
        tvqAmount: transActu.mont?.TVQ,
        apresTax: transActu.mont?.apresTax,
        noTPS: transActu.noTax?.noTPS,
        noTVQ: transActu.noTax?.noTVQ,
        modImpr: transActu.modImpr,
        modTrans: transActu.modTrans,
        preced: this.getPreviousSignature(),
      });
      transActu.signa = signatureData;
      const envirn = mevStorage.getEnvirn() || 'DEV';
      const storeIdForCert = mevStorage.getStoreId() || storeId;
      transActu.emprCertifSEV = storeIdForCert
        ? (await mevCertService.getActiveCertThumbprint(storeIdForCert, envirn) || '')
        : '';
      if (!transActu.emprCertifSEV) {
        transActu.emprCertifSEV = readThumbprintPointer(storeIdForCert, envirn) || '';
      }
      try {
        qrCodeUrl = await this.generateQRCodeUrl({
          ...transActu,
          store_id: storeIdForCert,
          EMPRCERTIFTRANSM: transActu.emprCertifSEV,
        }, envirn);
      } catch (error) {
        console.error('internet disconnected debug', 'offline qr failed', error);
      }
    }
    if (formattedData) formattedData.qrCodeUrl = qrCodeUrl;
    console.log('internet disconnected debug', 'formatted signed reqTrans', { offline, noTrans: transActu.noTrans });
    const merchantCopy = merchantCopyForPayload(transActu, {
      reqTrans: formattedData?.reqTrans,
      retourTrans: formattedData?.retourTrans,
    }, chosenMerchantCopy(working));
    console.log('Back to station', 'merchant copy', {
      noTrans: transActu.noTrans,
      typTrans: transActu.typTrans,
      modImpr: transActu.modImpr,
      enabled: merchantCopy.enabled,
    });
    logExceptionScenarios(event, 'format', transActu, source);
    if (merchantCopy.enabled) {
      logExceptionScenario('duplicate', 'format', { ...merchantCopy.data?.reqTrans?.transActu, modImpr: 'DUP' });
    }
    const job: OrderJob = {
      id: `offline-print-${order?.id || 'bill'}-${Date.now()}`,
      data: orderData,
      status: 'completed',
      createdAt: new Date(),
      processedAt: new Date(),
      billType,
      formattedData,
      result: {
        offline,
        responseData: { ...formattedData, _offline: offline },
        qrCodeUrl,
        qrCodeSize: 440,
        originalOrderData: orderData,
        merchantCopyEnabled: merchantCopy.enabled,
        merchantResponseData: merchantCopy.data,
      },
    };
    this.jobs.push(job);
    if (this.jobs.length > this.MAX_JOBS) this.jobs = this.jobs.slice(-this.MAX_JOBS);
    if (printReceipt && event !== 'kitchen') {
      console.log('internet disconnected debug', 'notifying printer listener', {
        jobId: job.id,
        noTrans: transActu.noTrans,
        typTrans: transActu.typTrans,
        formImpr: transActu.formImpr,
        listenerCount: this.eventListeners['job-processed']?.length || 0,
      });
      this.notifyListeners('job-processed', job);
    }
    return formattedData;
  }

  public emitOfflineReceipt(order: any, event: string): void {
    this.formatAndPrintBill(order, event, true).catch((error) => {
      console.error('internet disconnected debug', 'formatAndPrintBill failed', error);
    });
  }

  /**
   * Reprint order from Firestore (Duplicate receipt)
   */
  public reprintOrderFromFirestore(order: any, orderId: string): void {
    console.log(`[MEV] Reprinting order from Firestore: ${orderId}`);

    // Force reproduction mode in the converted data
    const mevOrderData = this.convertRmsPosOrderToMev(order, orderId);
    mevOrderData.modImpr = 'RPR';

    const newJob: OrderJob = {
      id: `${orderId}-reprint-${Date.now()}`,
      data: mevOrderData,
      status: 'pending',
      createdAt: new Date(),
      modImpr: 'RPR'
    };

    this.jobs.push(newJob);
    if (this.jobs.length > this.MAX_JOBS) this.jobs = this.jobs.slice(-this.MAX_JOBS);
    this.notifyListeners('job-received', newJob);

    this.processJob(newJob).catch(error => console.error('Error processing reprint job:', error));
  }

  /**
   * Process refund transaction (Credit Note)
   * Creates a MEV RFER transaction with negative amounts
   */
  public async processRefundTransaction(
    originalOrder: any,
    refundItems: any[],
    refundReason: string,
    currentUser: any,
    customRefundAmount?: number | null
  ): Promise<any> {
    console.log(`[MEV REFUND] Processing refund for order: ${originalOrder.id}`, {
      refundItems,
      refundReason,
      currentUser,
      customRefundAmount
    });

    let refundSubtotal: number;
    let refundTaxAmount: number;
    let refundTotal: number;
    let refundTPS: number;
    let refundTVQ: number;

    // Get tax breakdown rates first (needed for both custom and item-based refunds)
    const tpsRate = originalOrder.taxes?.find((t: any) => t.taxName?.toUpperCase() === 'TPS')?.taxRate || 0.05;
    const tvqRate = originalOrder.taxes?.find((t: any) => t.taxName?.toUpperCase() === 'TVQ')?.taxRate || 0.09975;
    const combinedTaxRate = tpsRate + tvqRate; // Combined rate: 0.05 + 0.09975 = 0.14975
    const savedLineTaxes = refundFromLines(originalOrder, refundItems);

    // If custom refund amount is provided, use it directly
    if (customRefundAmount !== null && customRefundAmount !== undefined && customRefundAmount > 0) {
      // For custom amount, we need to work backwards: total includes tax, so calculate subtotal
      // refundTotal = customRefundAmount (this is the total including tax)
      refundTotal = customRefundAmount;
      // Calculate subtotal from total: subtotal = total / (1 + TPS_rate + TVQ_rate)
      // This ensures: Total = Subtotal * (1 + TPS + TVQ)
      refundSubtotal = refundTotal / (1 + combinedTaxRate);

      // Round subtotal to 2 decimal places to ensure precision
      refundSubtotal = Math.round(refundSubtotal * 100) / 100;

      // Calculate tax breakdown from rounded subtotal (TPS and TVQ from subtotal)
      refundTPS = Math.round(refundSubtotal * tpsRate * 100) / 100;
      refundTVQ = Math.round(refundSubtotal * tvqRate * 100) / 100;

      // Recalculate total from rounded subtotal and rounded taxes to ensure consistency
      refundTaxAmount = refundTPS + refundTVQ;
      refundTotal = refundSubtotal + refundTaxAmount;
    } else {
      // Calculate refund totals from items
      refundSubtotal = refundItems.reduce((sum, item) => {
        const qty = item.quantity || item.qty || item.amount || 1;
        return sum + (item.price * qty);
      }, 0);

      // Round subtotal to 2 decimal places
      refundSubtotal = Math.round(refundSubtotal * 100) / 100;

      if (savedLineTaxes) {
        refundTPS = savedLineTaxes.tps;
        refundTVQ = savedLineTaxes.tvq;
        refundTaxAmount = savedLineTaxes.taxTotal;
      } else {
        refundTPS = Math.round(refundSubtotal * tpsRate * 100) / 100;
        refundTVQ = Math.round(refundSubtotal * tvqRate * 100) / 100;
        refundTaxAmount = refundTPS + refundTVQ;
      }

      // Calculate total from rounded values
      refundTotal = refundSubtotal + refundTaxAmount;
    }

    // Increment transaction number suffix
    const { generateTransactionNumber } = require('../../utils/auditLogger');
    const baseTransactionId = originalOrder.tableName || originalOrder.sessionId || originalOrder.id;
    const currentSuffix = originalOrder.printVersionSuffix || 0;
    const newSuffix = currentSuffix + 1;
    const refundTransactionNumber = generateTransactionNumber(baseTransactionId, newSuffix);

    console.log('[MEV REFUND] Transaction number increment:', {
      baseTransactionId,
      currentSuffix,
      newSuffix,
      refundTransactionNumber
    });

    // Create refund order data with negative amounts
    const refundOrderData: OrderData = {
      order: {
        order_id: refundTransactionNumber,
        tax_details: [
          { label: 'TPS', total: -refundTPS },
          { label: 'TVQ', total: -refundTVQ }
        ],
        items: customRefundAmount && customRefundAmount > 0
          ? [{
            qty: 1,
            name: 'Remboursement partiel',
            price: -refundSubtotal, // Negative price for refund (subtotal before taxes)
            sku: 'CUSTOM_REFUND'
          }]
          : refundItems.map((item: any) => ({
            qty: item.quantity || item.qty || item.amount || 1,
            name: item.name || '',
            price: -(item.price || 0), // Negative price for refund
            sku: item.id || '',
            ...(Number(item.seatNumber) > 0 ? { seatNumber: Number(item.seatNumber) } : {}),
            ...(LINE_TAX_CODES.includes(String(item.tax || '').toUpperCase()) ? { tax: String(item.tax).toUpperCase() } : {}),
            ...copyFiscalTags(item),
          })),
        sub_total: -refundSubtotal,
        tax_amount: -refundTaxAmount,
        total: -refundTotal,
        payment_method: {
          code: this.paymentCodeFromOrder(originalOrder) || 'ARG'
        },
        note: `REFUND: ${refundReason}`,
        order_channel: originalOrder.orderChannel,
        table_name: originalOrder.tableName,
        guest_count: originalOrder.guestCount,
        refunds: [{
          refund_id: `${originalOrder.id}-refund-${Date.now()}`,
          refund_date: new Date(),
          refunded_by: currentUser?.email || currentUser?.name || 'Unknown',
          reason: refundReason,
          items: customRefundAmount && customRefundAmount > 0
            ? [{
              item: { id: 'CUSTOM_REFUND', name: 'Remboursement partiel' },
              qty: 1,
              refund_total: refundTotal,
              refund_total_excl_tax: refundSubtotal
            }]
            : refundItems.map((item: any) => ({
              item: item,
              qty: item.quantity || item.qty || item.amount || 1,
              refund_total: (item.price || 0) * (item.quantity || item.qty || item.amount || 1),
              refund_total_excl_tax: (item.price || 0) * (item.quantity || item.qty || item.amount || 1) / (1 + (originalOrder.financials?.taxTotal / originalOrder.financials?.subtotal || 0))
            })),
          refund_total: refundTotal,
          refund_total_excl_tax: refundSubtotal,
          payments: [{
            payment_name: 'REMBOURSEMENT',
            amount: refundTotal
          }],
          mev_transaction_number: refundTransactionNumber
        }]
      },
      note: `REFUND: ${refundReason}`,
      session_id: originalOrder.sessionId || originalOrder.id,
      user_name: currentUser?.name || currentUser?.displayName || 'POS User',
      auditLog: originalOrder.auditLog || [],
      printHistory: originalOrder.printHistory || [],
      printVersionSuffix: newSuffix,
      isRevised: false,
      replacesCount: 0,
      tipAmount: 0,
      refs: (() => {
        const ref = this.priorBillRef(originalOrder);
        return ref ? [ref] : [];
      })(),
    };

    // Create job for refund transaction
    const refundJob: OrderJob = {
      id: `${originalOrder.id}-refund-${Date.now()}`,
      data: refundOrderData,
      status: 'pending',
      createdAt: new Date(),
      billType: 'final' // Refunds are always final transactions
    };

    this.jobs.push(refundJob);
    if (this.jobs.length > this.MAX_JOBS) this.jobs = this.jobs.slice(-this.MAX_JOBS);
    this.notifyListeners('job-received', refundJob);

    // Process the refund job and wait for completion
    await this.processJob(refundJob);

    if (refundJob.status === 'failed') {
      throw new Error(refundJob.error || 'Refund transaction failed');
    }

    console.log('[MEV REFUND] Refund transaction completed:', refundJob.result);

    return {
      success: true,
      transactionNumber: refundTransactionNumber,
      psiNoTrans: refundJob.result?.psiNoTrans,
      psiDatTrans: refundJob.result?.psiDatTrans,
      refundTotal: refundTotal,
      refundData: refundOrderData.order.refunds[0]
    };
  }

  /**
   * Process corrected receipt (MEV 2.3.10)
   * Sends a reverse transaction followed by a corrected transaction with new payment method
   */
  public async processCorrectedReceipt(
    originalOrder: any,
    newPaymentMethodName: string,
    currentUser: any,
    store?: any,
    paymentMethods?: any[]
  ): Promise<any> {
    console.log(`[MEV CORRECT] Processing corrected receipt for order: ${originalOrder.id}`, {
      originalPaymentMethod: originalOrder.paymentMethod,
      originalPaymentMethodCode: originalOrder.paymentMethodCode,
      newPaymentMethod: newPaymentMethodName,
      currentUser
    });

    // CRITICAL: Retrieve the original payment method code
    // Priority 1: Use stored paymentMethodCode field (new field)
    // Priority 2: Use financials.payment.methodCode (if stored there)
    // Priority 3: Lookup from store payment methods using paymentMethod name
    // DO NOT DEFAULT - use the actual stored code
    let originalPaymentMethodCode: string | null = null;

    if (originalOrder.paymentMethodCode) {
      // New field: directly stored code
      originalPaymentMethodCode = originalOrder.paymentMethodCode;
      console.log('[MEV CORRECT] Using stored paymentMethodCode:', originalPaymentMethodCode);
    } else if (originalOrder.financials?.payment?.methodCode) {
      // Stored in financials.payment.methodCode
      originalPaymentMethodCode = originalOrder.financials.payment.methodCode;
      console.log('[MEV CORRECT] Using financials.payment.methodCode:', originalPaymentMethodCode);
    } else if (originalOrder.paymentMethod && (store?.paymentMethods || paymentMethods)) {
      // Lookup from store payment methods using the stored name
      const methodsToSearch = paymentMethods || store?.paymentMethods || [];
      const paymentMethodObj = methodsToSearch.find((pm: any) => {
        return pm.name === originalOrder.paymentMethod ||
          pm.label === originalOrder.paymentMethod ||
          (pm.translationKey && pm.label === originalOrder.paymentMethod);
      });

      if (paymentMethodObj?.jsonCode) {
        originalPaymentMethodCode = paymentMethodObj.jsonCode;
        console.log('[MEV CORRECT] Looked up paymentMethodCode from store:', originalPaymentMethodCode);
      }
    }

    // CRITICAL: If we still don't have a code, throw an error - DO NOT DEFAULT
    if (!originalPaymentMethodCode) {
      throw new Error(
        `Cannot determine original payment method code for order ${originalOrder.id}. ` +
        `Payment method: ${originalOrder.paymentMethod}. ` +
        `Please ensure paymentMethodCode is stored with the order or provide store/paymentMethods.`
      );
    }

    // Get transaction number generator
    const { generateTransactionNumber } = require('../../utils/auditLogger');
    const baseTransactionId = originalOrder.tableName || originalOrder.sessionId || originalOrder.id;
    const currentSuffix = originalOrder.printVersionSuffix || 0;

    // Extract original transaction information for refs
    // The original transaction number should be the base transaction ID with suffix 0 (or current suffix if already incremented)
    // Get from printHistory (last print event) or generate from base with original suffix
    const printHistory = originalOrder.printHistory || [];
    const lastPrintEvent = printHistory.length > 0 ? printHistory[printHistory.length - 1] : null;

    // Original transaction number: use the transaction number from printHistory if available,
    // otherwise generate it from base with original suffix (which should be 0 for the first transaction)
    const originalSuffix = 0; // Original transaction always has suffix 0
    const originalTransactionNumber = lastPrintEvent?.transactionNumber ||
      generateTransactionNumber(baseTransactionId, originalSuffix);
    const originalDatTrans = lastPrintEvent?.datTrans || this.formatDateTime(new Date(originalOrder.created || Date.now()));
    const originalSubtotal = originalOrder.financials?.subtotal || originalOrder.sub_total || 0;

    // STEP 1: Create and send REVERSE transaction (negative amounts, original payment method)
    const reverseSuffix = currentSuffix + 1;
    const reverseTransactionNumber = generateTransactionNumber(baseTransactionId, reverseSuffix);

    console.log('[MEV CORRECT] Step 1: Reverse transaction', {
      baseTransactionId,
      currentSuffix,
      reverseSuffix,
      reverseTransactionNumber,
      originalTransactionNumber,
      originalDatTrans,
      originalSubtotal
    });

    // Create reverse order data with negative amounts
    const reverseOrderData: OrderData = {
      order: {
        order_id: reverseTransactionNumber,
        tax_details: (originalOrder.taxes || []).map((tax: any) => ({
          label: tax.taxName || '',
          total: -(tax.taxAmount || 0)
        })),
        items: (originalOrder.items || []).map((item: any) => ({
          qty: item.quantity || item.qty || item.amount || 1,
          name: item.name || '',
          price: -(item.price || 0), // Negative price for reverse
          sku: item.id || '',
          ...(Number(item.seatNumber) > 0 ? { seatNumber: Number(item.seatNumber) } : {}),
          ...copyFiscalTags(item),
        })),
        sub_total: -(originalOrder.financials?.subtotal || 0),
        tax_amount: -(originalOrder.financials?.taxTotal || 0),
        total: -(originalOrder.financials?.grandTotal || originalOrder.total || 0),
        payment_method: { code: 'AUC' },
        note: `CORRECTION: Reverse original transaction`,
        order_channel: originalOrder.orderChannel,
        table_name: originalOrder.tableName,
        guest_count: originalOrder.guestCount
      },
      note: `CORRECTION: Reverse original transaction`,
      session_id: originalOrder.sessionId || originalOrder.id,
      user_name: currentUser?.name || currentUser?.displayName || 'POS User',
      auditLog: originalOrder.auditLog || [],
      printHistory: originalOrder.printHistory || [],
      printVersionSuffix: reverseSuffix,
      isRevised: false,
      replacesCount: 0,
      tipAmount: 0,
      modImpr: 'FAC', // No printing for reverse transaction
      isReverse: true, // Flag to indicate this is a reverse transaction
      // Add refs pointing to original transaction
      refs: [{
        noTrans: originalTransactionNumber,
        datTrans: originalDatTrans,
        avantTax: this.formatAmount(originalSubtotal) // Positive value for original subtotal
      }]
    };

    // Create job for reverse transaction
    const reverseJob: OrderJob = {
      id: `${originalOrder.id}-reverse-${Date.now()}`,
      data: reverseOrderData,
      status: 'pending',
      createdAt: new Date(),
      billType: 'final',
      modImpr: 'FAC' // No printing
    };

    this.jobs.push(reverseJob);
    if (this.jobs.length > this.MAX_JOBS) this.jobs = this.jobs.slice(-this.MAX_JOBS);
    this.notifyListeners('job-received', reverseJob);

    // Process the reverse job and wait for completion
    await this.processJob(reverseJob);

    if (reverseJob.status === 'failed') {
      throw new Error(reverseJob.error || 'Reverse transaction failed');
    }

    // Get reverse transaction date - use the transaction date from the formatted data if available,
    // otherwise use current date/time (the reverse transaction's datTrans will be set during formatting)
    // For refs, we need the datTrans that was actually sent in the reverse transaction
    // Since we don't have direct access to it, we'll use the current date/time as approximation
    // The actual datTrans will be set by formatOrderData when processing the reverse job
    const reverseDatTrans = this.formatDateTime(new Date());
    const reverseSubtotal = -(originalOrder.financials?.subtotal || originalOrder.sub_total || 0);

    console.log('[MEV CORRECT] Step 1 completed: Reverse transaction sent', {
      reverseTransactionNumber,
      reverseDatTrans,
      reverseSubtotal
    });

    // STEP 2: Create and send CORRECTED transaction (positive amounts, new payment method)
    const correctedSuffix = reverseSuffix + 1;
    const correctedTransactionNumber = generateTransactionNumber(baseTransactionId, correctedSuffix);

    console.log('[MEV CORRECT] Step 2: Corrected transaction', {
      correctedSuffix,
      correctedTransactionNumber,
      newPaymentMethod: newPaymentMethodName,
      reverseTransactionNumber,
      reverseDatTrans
    });

    // Create corrected order data with positive amounts and new payment method
    const correctedOrderData: OrderData = {
      order: {
        order_id: correctedTransactionNumber,
        tax_details: (originalOrder.taxes || []).map((tax: any) => ({
          label: tax.taxName || '',
          total: tax.taxAmount || 0
        })),
        items: (originalOrder.items || []).map((item: any) => ({
          qty: item.quantity || item.qty || item.amount || 1,
          name: item.name || '',
          price: item.price || 0,
          sku: item.id || '',
          ...(Number(item.seatNumber) > 0 ? { seatNumber: Number(item.seatNumber) } : {}),
          ...copyFiscalTags(item),
        })),
        sub_total: originalOrder.financials?.subtotal || 0,
        tax_amount: originalOrder.financials?.taxTotal || 0,
        total: originalOrder.financials?.grandTotal || originalOrder.total || 0,
        payment_method: { code: newPaymentMethodName }, // NEW payment method (must be object with code)
        note: `CORRECTION: Payment method changed to ${newPaymentMethodName}`,
        order_channel: originalOrder.orderChannel,
        table_name: originalOrder.tableName,
        guest_count: originalOrder.guestCount
      },
      note: `CORRECTION: Payment method changed to ${newPaymentMethodName}`,
      session_id: originalOrder.sessionId || originalOrder.id,
      user_name: currentUser?.name || currentUser?.displayName || 'POS User',
      auditLog: originalOrder.auditLog || [],
      printHistory: originalOrder.printHistory || [],
      printVersionSuffix: correctedSuffix,
      isRevised: false,
      replacesCount: 0,
      tipAmount: originalOrder.tipAmount || 0,
      modImpr: 'FAC', // Print corrected receipt
      // Add flag to indicate this is a corrected receipt (for receipt rendering)
      isCorrected: true,
      // Add refs pointing to reverse transaction
      refs: [{
        noTrans: reverseTransactionNumber,
        datTrans: reverseDatTrans,
        avantTax: this.formatAmount(reverseSubtotal) // Negative value for reverse subtotal
      }]
    };

    // Create job for corrected transaction
    const correctedJob: OrderJob = {
      id: `${originalOrder.id}-corrected-${Date.now()}`,
      data: correctedOrderData,
      status: 'pending',
      createdAt: new Date(),
      billType: 'final',
      modImpr: 'FAC' // Print receipt
    };

    this.jobs.push(correctedJob);
    if (this.jobs.length > this.MAX_JOBS) this.jobs = this.jobs.slice(-this.MAX_JOBS);
    this.notifyListeners('job-received', correctedJob);

    // Process the corrected job and wait for completion
    await this.processJob(correctedJob);

    if (correctedJob.status === 'failed') {
      throw new Error(correctedJob.error || 'Corrected transaction failed');
    }

    console.log('[MEV CORRECT] Step 2 completed: Corrected receipt printed');

    return {
      success: true,
      reverseTransactionNumber,
      correctedTransactionNumber,
      psiNoTrans: correctedJob.result?.psiNoTrans,
      psiDatTrans: correctedJob.result?.psiDatTrans,
      newPaymentMethod: newPaymentMethodName
    };
  }

  /**
   * Convert RMS POS order format to MEV order format
   */
  private convertRmsPosOrderToMev(order: any, orderId: string): OrderData {
    const { generateTransactionNumber } = require('../../utils/auditLogger');

    console.log('[MEV SEAT] convertRmsPosOrderToMev - order data:', {
      orderChannel: order.orderChannel,
      sessionId: order.sessionId,
      tableName: order.tableName,
      seatNumber: order.seatNumber,
      printVersionSuffix: order.printVersionSuffix
    });

    let baseTransactionId = this.channelRoot(order) || String(orderId || '');
    if (baseTransactionId.startsWith('anon_')) {
      const count = String(order?.channelOrderNumber || '').replace(/^#/, '').trim()
        || (order?.counter != null ? String(order.counter).trim() : '');
      if (count) baseTransactionId = count;
    }
    const revision = Number(order.printVersionSuffix || 0);
    const { tableDocumentNumber } = require('../../utils/orderNumberGenerator');
    const channelUpper = String(order?.orderChannel || '').toUpperCase();
    const transactionNumber = (channelUpper === 'TABLE' || channelUpper === 'TBL')
      ? tableDocumentNumber(baseTransactionId, revision)
      : generateTransactionNumber(baseTransactionId, revision);
    console.log('[MEV] Final transaction number:', transactionNumber, { baseTransactionId, revision });

    // Extract payment method code - handle both string and array formats
    console.log('[MEV] Payment method raw data:', order.paymentMethod);

    // Payment method name to MEV code mapping (French names only - as per system design)
    const paymentMethodMap: { [key: string]: string } = {
      // Argent comptant (Cash)
      'argent comptant': 'ARG',
      'cash': 'ARG',

      // Carte de crédit (Credit Card)
      'carte de crédit': 'CRE',
      'carte de credit': 'CRE',
      'credit card': 'CRE',
      'credit': 'CRE',

      // Carte de débit (Debit Card)
      'carte de débit': 'DEB',
      'carte de debit': 'DEB',
      'debit card': 'DEB',
      'debit': 'DEB',

      // Paiement mixte (Mixed Payment)
      'paiement mixte': 'MIX',
      'mixed payment': 'MIX',

      // Transfert électronique de fonds (Electronic Funds Transfer)
      'transfert électronique de fonds': 'TFD',
      'transfert electronique de fonds': 'TFD',
      'electronic funds transfer': 'TFD',

      // Portefeuille électronique (E-Wallet)
      'portefeuille électronique': 'MVO',
      'portefeuille electronique': 'MVO',
      'e-wallet': 'MVO',

      // Cryptomonnaie (Cryptocurrency)
      'cryptomonnaie': 'CRY',
      'cryptocurrency': 'CRY',

      // Certificat-cadeau (Gift Certificate)
      'certificat-cadeau': 'CPR',
      'certificat cadeau': 'CPR',
      'gift certificate': 'CPR',

      // Programme de fidélisation (Loyalty Program)
      'programme de fidélisation': 'FID',
      'programme de fidelisation': 'FID',
      'loyalty program': 'FID',

      // Porté au compte (Charge to Account)
      'porté au compte': 'PAC',
      'porte au compte': 'PAC',
      'charge to account': 'PAC',

      // Chèque (Check)
      'chèque': 'CHQ',
      'cheque': 'CHQ',
      'check': 'CHQ',

      // Autre mode de paiement (Other Payment Method)
      'autre mode de paiement': 'AUT',
      'other payment method': 'AUT',

      // Aucun paiement (No Payment)
      'aucun paiement': 'AUC',
      'no payment': 'AUC',

      // Sans objet (Without Object)
      'sans objet': 'SOB',
    };

    const explicitPayment = this.paymentCodeFromOrder(order);
    let paymentCode = explicitPayment || 'SOB';

    if (explicitPayment) {
      console.log('[MEV] Payment method from stored code:', paymentCode);
    } else if (typeof order.paymentMethod === 'string') {
      // Payment method is a string like "Argent comptant"
      const normalizedName = order.paymentMethod.toLowerCase().trim();
      paymentCode = paymentMethodMap[normalizedName] || 'AUT';
      console.log('[MEV] Payment method is string:', order.paymentMethod, '→', paymentCode);
    } else if (Array.isArray(order.paymentMethod) && order.paymentMethod.length > 0) {
      // Payment method is an array
      const pm = order.paymentMethod[0];
      if (pm?.code) {
        paymentCode = pm.code;
        console.log('[MEV] Payment method from array code:', paymentCode);
      } else if (pm?.jsonCode) {
        paymentCode = pm.jsonCode || 'AUT';
        console.log('[MEV] Payment method from array jsonCode:', paymentCode);
      } else if (pm?.name) {
        const normalizedName = pm.name.toLowerCase().trim();
        paymentCode = paymentMethodMap[normalizedName] || 'AUT';
        console.log('[MEV] Payment method from array name:', pm.name, '→', paymentCode);
      }
    } else if (order.paymentMethod && typeof order.paymentMethod === 'object') {
      // Payment method is an object
      if (order.paymentMethod.code) {
        paymentCode = order.paymentMethod.code;
      } else if (order.paymentMethod.jsonCode) {
        paymentCode = order.paymentMethod.jsonCode || 'AUT';
      } else if (order.paymentMethod.name) {
        const normalizedName = order.paymentMethod.name.toLowerCase().trim();
        paymentCode = paymentMethodMap[normalizedName] || 'AUT';
      }
    }

    console.log('[MEV] Final payment code:', paymentCode);

    const items: OrderItem[] = [];
    (order.items || []).forEach((item: any) => {
      const qty = item.amount || item.qty || 1;
      const unit = Number(item.price) || 0;
      const seatNumber = Number(item.seatNumber);
      items.push({
        qty,
        name: item.name || item.printName || '',
        price: unit,
        sku: item.sku || item.id || '',
        id: item.id || item.sku || '',
        tax: item.tax,
        preci: item.preci,
        ...(typeof item.special_instructions === 'string' ? { special_instructions: item.special_instructions } : {}),
        ...(Number.isFinite(seatNumber) ? { seatNumber } : {}),
        ...(Array.isArray(item.taxIds) ? { taxIds: item.taxIds } : {}),
        ...copyFiscalTags(item),
      });
      const percent = Number(item.itemDiscount) || 0;
      if (percent > 0 && unit !== 0) {
        const discount = Math.round(unit * (percent / 100) * qty * 100) / 100;
        if (discount !== 0) {
          items.push({
            qty: 1,
            name: 'Rabais',
            price: -discount,
            sku: 'RABAIS',
            ...(Array.isArray(item.taxIds) ? { taxIds: item.taxIds } : {}),
          });
        }
      }
    });

    const subTotal = order.financials?.subtotal ?? order.subtotal ?? order.total ?? 0;
    const taxAmount = order.financials?.taxTotal ?? order.taxTotal ?? (order.taxes?.reduce((sum: number, tax: any) => sum + (Number(tax.taxAmount ?? tax.amount) || 0), 0) || 0);
    const grandTotal = order.financials?.grandTotal ?? order.totalWithTaxes ?? order.total ?? (subTotal + taxAmount);

    const mevOrder: Order = {
      order_id: transactionNumber,  // Use same logic as temporary bills
      tax_details: (order.taxes || []).map((tax: any) => ({
        label: tax.taxName || tax.name || '',
        total: Number(tax.taxAmount ?? tax.amount) || 0,
        ...(tax.fiscalType ? { fiscalType: tax.fiscalType } : {}),
      })),
      items: items,
      sub_total: subTotal,
      tax_amount: taxAmount,
      total: grandTotal,
      payment_method: { code: paymentCode },
      note: order.note || '',
      ...(typeof order.special_instructions === 'string' ? { special_instructions: order.special_instructions } : {}),
      order_channel: order.orderChannel,
      table_name: order.tableName,
      guest_count: order.guestCount,
      seat_number: Number(order.seatNumber) > 0 ? Number(order.seatNumber) : null,
    };

    let billAdjustment = 0;
    const feesAndDiscounts = order.feesAndDiscounts;
    if (feesAndDiscounts && typeof feesAndDiscounts === 'object' && !Array.isArray(feesAndDiscounts)) {
      Object.values(feesAndDiscounts).forEach((entry: any) => {
        const amount = Number(entry?.amount) || 0;
        if (entry?.type === 'discount') billAdjustment -= amount;
        else if (entry?.type === 'fee') billAdjustment += amount;
      });
    }

    return {
      order: mevOrder,
      note: order.note || '',
      session_id: order.sessionId || order.id,
      user_name: order.posUserName || order.user_name,
      auditLog: order.auditLog || [],
      printHistory: order.printHistory || [],
      printVersionSuffix: order.printVersionSuffix,
      isRevised: order.isRevised != null ? Boolean(order.isRevised) : (order.printVersionSuffix || 0) > 0,
      replacesCount: order.replacesCount != null ? Number(order.replacesCount) : (order.printHistory?.length || 0),
      tipAmount: order.tipAmount || 0,
      modImpr: order.modImpr || 'FAC',
      formImpr: order.formImpr,
      refs: order.refs,
      isReverse: Boolean(order.isReverse),
      isCorrected: Boolean(order.isCorrected),
      billAdjustment,
      receiptDelivery: order.receiptDelivery || null,
      receiptEmail: order.receiptEmail || order.customerEmail || order.customer?.email || null,
      ...(typeof order.merchantCopy === 'boolean' ? { merchantCopy: order.merchantCopy } : {}),
      sourceChannel: order.orderChannel || order.channel || null,
      serviceType: order.serviceType || order.fulfillment?.serviceType || null,
      onlineOrderChannel: order.onlineOrderChannel || null,
      platform: order.platform || null,
      paymentStatus: order.paymentStatus || order.financials?.payment?.status || null,
      paymentMethodRaw: order.paymentMethod ?? order.financials?.payment?.method ?? null,
      paymentMethodCode: order.paymentMethodCode || order.financials?.payment?.methodCode || null,
      onlineCheckoutMethod: order.onlineCheckoutMethod || null,
      onlineCheckoutLabel: order.onlineCheckoutLabel || null,
      isPaid: order.isPaid,
      storeTaxes: order.storeTaxes || [],
    };
  }

  /**
   * Convert cart items from React state to MEV OrderData format
   */
  public convertCartToMev(cartItems: any[], store: any, totals: { total: number; totalWithTaxes: number; taxes: any[] }, session?: any): OrderData {
    const items: OrderItem[] = (cartItems || []).map((item: any) => ({
      qty: item.amount || item.qty || 1,
      name: item.name || item.printName || '',
      price: item.price || 0,
      sku: item.id || '',
      ...(Number(item.seatNumber) > 0 ? { seatNumber: Number(item.seatNumber) } : {}),
      ...(Array.isArray(item.taxIds) ? { taxIds: item.taxIds } : {}),
      ...copyFiscalTags(item),
    }));

    // Generate transaction number with suffix if provided
    const transactionNumber = session?.transactionNumber || session?.tableName || 'TEMP';

    const mevOrder: Order = {
      order_id: transactionNumber,  // Use transaction number with suffix
      tax_details: (totals.taxes || []).map((tax: any) => ({
        label: tax.taxName || '',
        ...(tax.fiscalType ? { fiscalType: tax.fiscalType } : {}),
        total: tax.taxAmount || 0
      })),
      items: items,
      sub_total: totals.total,
      tax_amount: totals.taxes?.reduce((sum: number, tax: any) => sum + (tax.taxAmount || 0), 0) || 0,
      total: totals.totalWithTaxes,
      payment_method: { code: 'SOB' },
      order_channel: session?.orderChannel,
      table_name: session?.tableName,
      guest_count: session?.guests || session?.guestCount,
      session_id: session?.id || session?.sessionId
    };

    return {
      order: mevOrder,
      note: session?.tableName ? `Table: ${session.tableName}` : '',
      session_id: session?.id || session?.sessionId,
      user_name: session?.user?.name || session?.user?.displayName,
      auditLog: session?.auditLog || [],
      printHistory: session?.printHistory || [],
      printVersionSuffix: session?.printVersionSuffix || 0,
      isRevised: (session?.printVersionSuffix || 0) > 0,
      replacesCount: session?.replacesCount || 0,
      modImpr: session?.modImpr || 'FAC',
      storeTaxes: store?.taxes || []  // Pass store taxes for alcohol detection
    };
  }

  /**
   * Save transaction to offline queue when connection fails
   */
  private async saveToOfflineQueue(formattedData: any, job: OrderJob): Promise<void> {
    try {
      console.log('[MEV Offline] Saving transaction to offline queue');

      // Extract metadata from formatted data
      const transActu = formattedData?.reqTrans?.transActu;
      if (!transActu) {
        throw new Error('Transaction data missing - cannot save to offline queue');
      }

      const metadata = {
        orderId: job.id,
        sessionId: job.data?.session_id || job.data?.sessionId || '',
        transactionNumber: transActu.noTrans || '',
        datTrans: transActu.datTrans || '',
        createdAt: Date.now(),
        storeId: job.data?.store_id || job.data?.storeId || localStorage.getItem('selectedStoreId') || '',
        userId: job.data?.user_id || job.data?.userId || '',
      };

      // Save to queue
      await mevOfflineQueue.addTransaction(formattedData, metadata);

      // Enter offline mode
      mevOfflineQueue.enterOfflineMode();

      console.log('[MEV Offline] Transaction saved to queue:', metadata.transactionNumber);
    } catch (error) {
      console.error('[MEV Offline] Failed to save transaction to offline queue:', error);
      throw error;
    }
  }

  private parseStoreId(value: unknown): string {
    if (!value) return '';
    if (typeof value === 'object' && (value as any).id) return String((value as any).id);
    const text = String(value).trim();
    if (!text) return '';
    if (text.startsWith('{')) {
      try {
        const parsed = JSON.parse(text);
        return String(parsed.id || parsed.storeId || '');
      } catch {
        return '';
      }
    }
    return text;
  }

  private resolveJobStoreId(job: OrderJob): string {
    return this.parseStoreId(
      job.data?.store_id ||
      job.data?.storeId ||
      job.data?.order?.storeId ||
      localStorage.getItem('selectedStoreId') ||
      localStorage.getItem('currentStoreId') ||
      mevStorage.getStoreId()
    );
  }

  private isPathEnabled(key: 'mevClientEnabled' | 'cloudMevEnabled'): boolean {
    try {
      const storeId = this.parseStoreId(
        localStorage.getItem('selectedStoreId') ||
        localStorage.getItem('currentStoreId') ||
        mevStorage.getStoreId()
      );
      if (!storeId) return true;
      const raw = localStorage.getItem(`${key}_${storeId}`);
      if (raw === 'false') return false;
      return true;
    } catch {
      return true;
    }
  }

  private canUseLocalMtls(): boolean {
    return typeof window !== 'undefined' && typeof (window as any).api?.performMutualTlsRequest === 'function';
  }

  private resolveCloudMevEnvirn(storeId: string): string {
    let fromStore = "";
    try {
      const raw = localStorage.getItem('selectedStore') || localStorage.getItem('currentStore');
      if (raw) {
        const store = JSON.parse(raw);
        fromStore = String(store?.cloudMevEnvirn || "");
      }
    } catch {
      fromStore = "";
    }
    return resolveCloudMevEnv(fromStore, storeId);
  }

  private getCurrentPosUser(): { id: string | null; name: string } {
    try {
      const storeId = mevStorage.getStoreId();
      if (storeId) {
        const posUserData = localStorage.getItem(`currentPOSUser_${storeId}`);
        if (posUserData) {
          const posUser = JSON.parse(posUserData);
          return { id: posUser.id || null, name: posUser.name || 'POS User' };
        }
      }
      const userData = JSON.parse(mevStorage.getPOSUser('') || '{}');
      return {
        id: userData.id || null,
        name: userData.name || userData.displayName || userData.username || 'POS User',
      };
    } catch {
      return { id: null, name: 'POS User' };
    }
  }

  private mapJobToCloudEvent(job: OrderJob): CloudMevTransactionEvent {
    const items = job.data?.order?.items || job.data?.items || [];
    const note = String(job.data?.note || job.data?.order?.note || '');
    if (job.billType === 'temporary') return 'temporary';
    if (job.billType === 'cancellation' || job.modImpr === 'ANN' || job.data?.modImpr === 'ANN') return 'cancel_before_print';
    if (job.modImpr === 'RPR' || job.data?.modImpr === 'RPR') return 'reprint';
    if (note.startsWith('REFUND:') || items.some((item: any) => item?.sku === 'CUSTOM_REFUND')) return 'refund';
    if (items.some((item: any) => item?.sku === 'ANNULATION' || item?.name === 'Annulation')) return 'cancel_after_print';
    return 'final';
  }

  private async resolveEnrolledCloudEnv(storeId: string, preferred: string): Promise<string> {
    if (!storeId) return preferred;
    const key = `${storeId}:${preferred}`;
    const now = Date.now();
    if (this.cloudEnrollCache && this.cloudEnrollCache.key === key && now - this.cloudEnrollCache.at < 30000) {
      return this.cloudEnrollCache.enrolled ? (this.cloudEnrollCache as any).envirn || preferred : preferred;
    }
    try {
      const found = await findEnrolledCloudMevEnv(storeId, preferred);
      const envirn = found?.envirn || preferred;
      this.cloudEnrollCache = { key, enrolled: Boolean(found), at: now, envirn, idApprl: found?.idApprl } as any;
      return envirn;
    } catch (error) {
      console.warn('[Cloud MEV] Enrollment lookup failed:', error);
      this.cloudEnrollCache = { key, enrolled: false, at: now };
      return preferred;
    }
  }

  /**
   * Send the live-client sale through the Cloud Function instead of local mTLS.
   * Returns true when the cloud path handled the job (caller must return).
   */
  private async processCloudMevJob(job: OrderJob): Promise<boolean> {
    if (!this.isPathEnabled('cloudMevEnabled')) return false;
    const storeId = this.resolveJobStoreId(job);
    const preferredEnv = this.resolveCloudMevEnvirn(storeId);
    const envirn = await this.resolveEnrolledCloudEnv(storeId, preferredEnv);
    const enrolled = Boolean(this.cloudEnrollCache?.enrolled);
    const mustUseCloud = enrolled || !this.canUseLocalMtls() || !this.isPathEnabled('mevClientEnabled');
    if (!mustUseCloud) return false;
    if (!storeId) {
      this.emitCloudJson({
        ok: false,
        event: this.mapJobToCloudEvent(job),
        at: new Date().toISOString(),
        error: 'Cloud MEV needs a store id. Select a store and try again.',
      });
      job.status = 'failed';
      job.error = 'Missing store id';
      return true;
    }

    const event = this.mapJobToCloudEvent(job);
    this.emitCloudJson({
      ok: true,
      event,
      at: new Date().toISOString(),
      pending: true,
      rqResponse: { status: 'sending', envirn, event },
    });
    const posUser = this.getCurrentPosUser();
    const sale = job.data || {};
    const extras: Record<string, unknown> = {
      saleReady: true,
      billType: job.billType || (event === 'temporary' ? 'temporary' : event === 'cancel_before_print' ? 'cancellation' : 'final'),
      modImpr: job.modImpr || sale.modImpr,
      refs: sale.refs,
      printHistory: sale.printHistory,
      isReverse: Boolean(sale.isReverse),
      isRevised: Boolean(sale.isRevised),
      trainingMode: isUserInTrainingMode(posUser.id),
      userName: sale.user_name || posUser.name,
      storeTaxes: sale.storeTaxes,
      paymentMethod: sale.order?.payment_method?.code || sale.payment_method?.code,
    };

    const emitOverlay = (payload: Record<string, unknown>) => {
      this.emitCloudJson({
        event,
        at: new Date().toISOString(),
        ...payload,
      });
    };

    try {
      const savedCert = loadSavedCloudMevCertRecord(storeId, envirn);
      const status = await getCloudMevStoreStatus(storeId, envirn).catch(() => null);
      const idApprl = pickIssuedIdapprl(
        this.cloudEnrollCache && (this.cloudEnrollCache as any).idApprl,
        savedCert.idApprl,
        savedCert.cert?.idapprl,
        status?.idApprl,
        status?.snapshot?.idApprl,
        status?.snapshot?.cert?.idapprl,
        status?.snapshot?.transHeader?.IDAPPRL,
        status?.snapshot?.headers?.IDAPPRL
      );
      const business = status?.snapshot?.business || {};
      const result = await sendCloudMevTransaction({
        storeId,
        envirn,
        event,
        order: sale,
        extras,
        headers: { CASESSAI: caseForEssaiHeader(envirn, storeId), ...(idApprl ? { IDAPPRL: idApprl } : {}) },
        idApprl,
        business,
      });
      const rqResponse = result.rqResponse || result.rqBody || result;
      const actu = rqResponse?.retourTrans?.retourTransActu || {};
      job.status = 'completed';
      job.processedAt = new Date();
      job.result = {
        cloud: true,
        cloudMevOverlay: true,
        event,
        psiNoTrans: actu.psiNoTrans,
        psiDatTrans: actu.psiDatTrans,
        rqHeaders: result.rqHeaders,
        rqRequest: result.rqRequest,
        rqResponse,
        rqStatus: result.rqStatus,
        rqUrl: result.rqUrl,
        exchanges: result.exchanges,
        responseData: rqResponse,
      };
      emitOverlay({
        ok: true,
        rqStatus: result.rqStatus,
        rqUrl: result.rqUrl,
        rqHeaders: result.rqHeaders,
        rqRequest: result.rqRequest,
        rqResponse,
        exchanges: result.exchanges,
      });
      return true;
    } catch (error: any) {
      const serialized = serializeCloudMevError(error);
      const details = (serialized.details || {}) as Record<string, any>;
      job.status = 'failed';
      job.processedAt = new Date();
      job.error = serialized.message;
      job.result = {
        cloud: true,
        cloudMevOverlay: true,
        ok: false,
        event,
        rqHeaders: details.rqHeaders,
        rqRequest: details.rqRequest,
        rqResponse: details.rqResponse || details,
        rqStatus: details.rqStatus,
        rqUrl: details.rqUrl,
        exchanges: details.exchanges,
        error: serialized.message,
      };
      emitOverlay({
        ok: false,
        error: serialized.message,
        rqHeaders: details.rqHeaders,
        rqRequest: details.rqRequest,
        rqResponse: details.rqResponse || details,
        rqStatus: details.rqStatus,
        rqUrl: details.rqUrl,
        exchanges: details.exchanges,
        details: serialized,
      });
      return true;
    }
  }

  /**
   * Process a job by formatting the order data and sending it to the MEV endpoint
   */
  private async processJob(job: OrderJob): Promise<void> {
    if (job.status !== 'pending') return;
    job.status = 'failed';
    job.error = 'Local MEV send is retired. Cloud Functions send to Revenu Québec.';
    job.processedAt = new Date();
    this.notifyListeners('job-failed', job);
  }

  private async processJobRetired(job: OrderJob): Promise<void> {
    if (job.status !== 'pending') return;
    try {
      job.status = 'processing';
      this.notifyListeners('status-changed', job);

      const cloudOn = this.isPathEnabled('cloudMevEnabled');
      const clientOn = this.isPathEnabled('mevClientEnabled');
      if (!cloudOn && !clientOn) {
        job.status = 'failed';
        job.error = 'MEV Client and Cloud MEV are both turned off';
        job.processedAt = new Date();
        this.notifyListeners('job-failed', job);
        return;
      }

      if (await this.processCloudMevJob(job)) {
        return;
      }

      if (!clientOn) {
        job.status = 'failed';
        job.error = 'MEV Client is turned off';
        job.processedAt = new Date();
        this.notifyListeners('job-failed', job);
        return;
      }

      // Log network status for debugging
      const isOnline = connectionMonitor.getStatus();
      console.log(`[OFFLINE] Network Status - connectionMonitor.getStatus(): ${isOnline}`);

      // CRITICAL: Check offline FIRST before formatOrderData (which generates signatures!)
      if (!isOnline) {
        console.warn('[MEV] System OFFLINE - using dummy signatures for receipt');

        // Format data (will use dummy signatures from generateTransactionSignature)
        const formattedData = await this.formatOrderData(job.data, job.billType, job.modImpr);

        // Generate QR code even when offline (uses local transaction data)
        const qrCodeUrl = await this.generateQRCodeUrl({
          ...(formattedData.reqTrans.transActu || {}),
          EMPRCERTIFTRANSM: formattedData.reqTrans.transActu.emprCertifSEV
        }, localStorage.getItem('envirn') || 'DEV');

        // Save to offline queue
        await this.saveToOfflineQueue(formattedData, job);

        // Structure result to match online format for receipt printing
        job.status = 'completed';
        job.processedAt = new Date();

        // Add offline flag directly to responseData for receipt renderer
        const responseDataWithOfflineFlag = {
          ...formattedData,
          _offline: true  // Internal flag for receipt rendering
        };

        const offlineTransActu = formattedData?.reqTrans?.transActu || {};
        const merchantCopy = merchantCopyForPayload(offlineTransActu, {
          reqTrans: formattedData?.reqTrans,
          retourTrans: formattedData?.retourTrans,
        }, chosenMerchantCopy(job.data));
        job.result = {
          offline: true,
          responseData: responseDataWithOfflineFlag,
          qrCodeUrl,
          qrCodeSize: 440,
          originalOrderData: job.data,
          merchantCopyEnabled: merchantCopy.enabled,
          merchantResponseData: merchantCopy.data,
        };

        this.notifyListeners('job-processed', job);
        return;
      }

      const formattedData = await this.formatOrderData(job.data, job.billType, job.modImpr);
      console.log('MEV: Formatted Data', JSON.stringify(formattedData, null, 2));
      job.formattedData = formattedData;

      // Check if this is an AUC (no payment) transaction with PSP mode
      const modPai = formattedData?.reqTrans?.transActu?.modPai;
      const modImpr = formattedData?.reqTrans?.transActu?.modImpr;
      const isAucNoPayment = modPai === 'AUC' && modImpr === 'PSP';

      const envirn = mevStorage.getEnvirn();
      const lowerEnv = envirn.toLowerCase();

      // Get private key and certificate from secure memory cache / Windows Store
      // Try multiple sources for store ID
      // CRITICAL: 'selectedStore' is what StoreContext actually uses!
      const storeId = job.data?.store_id || mevStorage.getStoreId() || '';

      console.log('[build debug] ========================================');
      console.log('[build debug] processJob - Store ID lookup');
      console.log('[build debug] ========================================');
      console.log('[build debug] - job.data.store_id:', job.data?.store_id);
      console.log('[build debug] - localStorage.selectedStore:', localStorage.getItem('selectedStore'));
      console.log('[build debug] - localStorage.selectedStoreId:', localStorage.getItem('selectedStoreId'));
      console.log('[build debug] - localStorage.currentStoreId:', localStorage.getItem('currentStoreId'));
      console.log('[build debug] - FINAL storeId:', storeId);

      if (!storeId) {
        console.error('[build debug] ❌ Store ID is NULL after checking ALL sources!');
        console.error('[MEV Job] Store ID not found. Checked: job.data.store_id, selectedStore, selectedStoreId, currentStoreId');
        throw new Error('Store ID not found. Please ensure a store is selected.');
      }

      console.log('[build debug] ✅ Store ID found:', storeId);

      console.log(`[MEV Job] Loading certificate and private key from memory cache for ${storeId}/${lowerEnv}`);

      const activePrivateKey = await getPrivateKey(storeId, lowerEnv);
      const certif = await getCertificate(storeId, lowerEnv);

      if (!activePrivateKey || !certif) {
        throw new Error('Active certificate and private key are required');
      }

      console.log(`[MEV Job] ✓ Certificate and private key loaded from memory cache`);
      const transHeaderKey = this.getEnvKey('transHeader', envirn);
      console.log('[build debug] Looking for transaction header with key:', transHeaderKey);
      console.log('[build debug] - envirn:', envirn);
      console.log('[build debug] - Checking localStorage for all transHeader keys...');

      // Check all possible transaction header keys
      const possibleKeys = ['transHeader_dev', 'transHeader_DEV', 'transHeader_prod', 'transHeader_PROD', 'transHeader'];
      possibleKeys.forEach(key => {
        const value = localStorage.getItem(key);
        console.log(`[build debug]   - ${key}:`, value ? 'EXISTS' : 'null');
      });

      const savedTransHeader = mevStorage.getTransactionHeader() || '';
      console.log('[build debug] - savedTransHeader:', savedTransHeader ? 'FOUND' : 'NULL');

      if (!savedTransHeader) {
        console.error('[build debug] ❌ Transaction header not found!');
        console.error('[build debug] - Tried key:', transHeaderKey);
        throw new Error('Transaction header information is required');
      }

      const transHeader = JSON.parse(savedTransHeader);
      const actu = formattedData?.reqTrans?.transActu?.signa?.actu;
      if (!actu) throw new Error("Failed to generate SIGNATRANSM: 'actu' missing");

      // MEV requires signatures to be exactly 88 characters
      const normalizeSignature = (sig: string): string => {
        if (sig.length === 88) return sig;
        if (sig.length > 88) return sig.substring(0, 88);
        return sig.padEnd(88, '=');
      };

      const signaTransmData = transHeader.authCode + transHeader.IDAPPRL + actu;
      const signResult = await window.api.signData(signaTransmData, activePrivateKey);
      if (!signResult || !signResult.success || !signResult.signature) {
        throw new Error(`Failed to generate SIGNATRANSM: ${signResult?.error || 'IPC failure'}`);
      }

      const signaTransm = normalizeSignature(signResult.signature);
      console.log('MEV: SIGNATRANSM', {
        original: signResult.signature,
        originalLength: signResult.signature.length,
        normalized: signaTransm,
        normalizedLength: signaTransm.length
      });

      // Get fingerprint (thumbprint) from memory cache (already loaded with certificate)
      // This avoids querying Windows Store or recalculating with OpenSSL
      let EMPRCERTIFTRANSM = await certificateMemoryCache.getThumbprint(storeId, lowerEnv);

      if (!EMPRCERTIFTRANSM) {
        // Not in cache - load certificate which will also cache thumbprint
        console.warn('[MEV] Thumbprint not in cache, loading certificate...');
        await certificateMemoryCache.loadPrivateKey(storeId, lowerEnv);
        EMPRCERTIFTRANSM = await certificateMemoryCache.getThumbprint(storeId, lowerEnv);

        if (!EMPRCERTIFTRANSM) {
          throw new Error(`Failed to load thumbprint for ${storeId}/${lowerEnv}`);
        }
      }

      const productHeaders = await ensureRqProduct(storeId);
      const options = {
        url: envirn === 'PROD' ? TRANSACTION_ENDPOINT_PROD : TRANSACTION_ENDPOINT,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ENVIRN: envirn,
          CASESSAI: caseForEssaiHeader(envirn, storeId),
          NOTPS: transHeader.noTPS,
          NOTVQ: transHeader.noTVQ,
          APPRLINIT: productHeaders.APPRLINIT,
          IDAPPRL: transHeader.IDAPPRL,
          IDSEV: productHeaders.IDSEV,
          IDVERSI: productHeaders.IDVERSI,
          CODCERTIF: productHeaders.CODCERTIF,
          IDPARTN: productHeaders.IDPARTN,
          VERSI: productHeaders.VERSI,
          VERSIPARN: productHeaders.VERSIPARN,
          SIGNATRANSM: signaTransm,
          EMPRCERTIFTRANSM: EMPRCERTIFTRANSM
        },
        body: JSON.stringify(formattedData),
        cert: certif,
        key: activePrivateKey,
        timeout: getApiWaitTime() // Apply configured wait time (in milliseconds)
      };

      console.log(`[MEV] === CERTIFICATE DIAGNOSTICS ===`);
      console.log(`[MEV] Store ID: ${storeId} | Env: ${lowerEnv}`);
      console.log(`[MEV] Header EMPRCERTIFTRANSM: ${EMPRCERTIFTRANSM}`);
      console.log(`[MEV] Certificate Length: ${certif.length}`);
      console.log(`[MEV] Private Key Length: ${activePrivateKey.length}`);
      console.log(`[MEV] =================================`);

      console.log('[MEV] API Request - Headers:', JSON.stringify(options.headers, null, 2));
      console.log('[MEV] API Request - Endpoint:', options.url);
      console.log('[MEV] API Request - Body:', JSON.stringify(formattedData, null, 2));

      // AUTOMATIC BATCH SYNC: Check if there are queued offline transactions
      // If yes, bundle them with the current transaction before sending
      const queueCount = await mevOfflineQueue.getQueueCount();
      if (queueCount > 0) {
        console.log(`[MEV] Found ${queueCount} queued offline transactions - bundling with current transaction`);

        try {
          // Get all unsent transactions
          const unsentTransactions = await mevOfflineQueue.getUnsentTransactions();

          // Build transLot array - strictly compliant with MEV API schema
          const transLot = unsentTransactions
            .sort((a, b) => b.metadata.datTrans.localeCompare(a.metadata.datTrans))
            .map(t => t.payload.reqTrans.transActu);

          // Add transLot to current transaction
          formattedData.reqTrans.transLot = transLot;

          // Update request body with batch
          options.body = JSON.stringify(formattedData);

          console.log(`[MEV] Bundled ${transLot.length} offline transactions with current transaction`);
        } catch (batchError) {
          console.error('[MEV] Failed to bundle offline transactions:', batchError);
          // Continue with current transaction only - don't fail the whole request
        }
      }

      // For AUC (no payment) with PSP mode, send two requests:
      // 1. First request: formImpr: "NON" (no printing), no REFS
      // 2. Second request: formImpr: "PAP" (paper printing), with REFS only if temporary bill exists
      let response;
      if (isAucNoPayment) {
        console.log('[MEV] AUC no payment detected - sending two sequential requests');

        // Get current user ID for training mode check
        // Get from job.data (orderData) or localStorage
        let aucCurrentUserId: string | null = null;
        try {
          const orderData = job.data;
          const storeId = orderData?.store_id || orderData?.storeId || localStorage.getItem('selectedStoreId');
          if (storeId) {
            const posUserData = localStorage.getItem(`currentPOSUser_${storeId}`);
            if (posUserData) {
              const posUser = JSON.parse(posUserData);
              aucCurrentUserId = posUser.id || null;
            }
          }
        } catch (error) {
          console.error('[MEV] Error getting current user ID for AUC:', error);
        }

        // First request: Transaction without printing
        const firstRequestData = JSON.parse(JSON.stringify(formattedData));
        firstRequestData.reqTrans.transActu.formImpr = 'NON';
        // Remove REFS from first request
        if (firstRequestData.reqTrans.transActu.refs) {
          delete firstRequestData.reqTrans.transActu.refs;
        }

        // Regenerate signature for first request
        const firstSignatureData = await this.generateTransactionSignature({
          noTrans: firstRequestData.reqTrans.transActu.noTrans,
          datTrans: firstRequestData.reqTrans.transActu.datTrans,
          tpsAmount: firstRequestData.reqTrans.transActu.mont.TPS,
          tvqAmount: firstRequestData.reqTrans.transActu.mont.TVQ,
          apresTax: firstRequestData.reqTrans.transActu.mont.apresTax,
          noTPS: transHeader['noTPS'],
          noTVQ: transHeader['noTVQ'],
          modImpr: 'PSP',
          modTrans: isUserInTrainingMode(aucCurrentUserId) ? 'FOR' : 'OPE',
          preced: this.getPreviousSignature()
        });
        firstRequestData.reqTrans.transActu.signa = firstSignatureData;

        // Update SIGNATRANSM for first request
        const firstActu = firstRequestData.reqTrans.transActu.signa.actu;
        const firstSignaTransmData = transHeader.authCode + transHeader.IDAPPRL + firstActu;
        const firstSignResult = await window.api.signData(firstSignaTransmData, activePrivateKey);
        if (!firstSignResult || !firstSignResult.success || !firstSignResult.signature) {
          throw new Error(`Failed to generate SIGNATRANSM for first request: ${firstSignResult?.error || 'IPC failure'}`);
        }
        const firstSignaTransm = normalizeSignature(firstSignResult.signature);

        const firstOptions = {
          ...options,
          headers: {
            ...options.headers,
            SIGNATRANSM: firstSignaTransm
          },
          body: JSON.stringify(firstRequestData)
        };

        console.log('[MEV] Sending first request (no printing):', JSON.stringify(firstRequestData, null, 2));

        let firstResponse;
        try {
          firstResponse = await window.api.performMutualTlsRequest(firstOptions);
          await logAPIResponse(
            firstOptions.url,
            'POST',
            firstRequestData,
            typeof firstResponse.data === 'string' ? JSON.parse(firstResponse.data) : firstResponse.data,
            100, // Approximate duration
            200
          );
        } catch (apiError: any) {
          // Check if this is a timeout or connection error
          const isConnectionError = apiError?.message?.includes('timeout') ||
            apiError?.message?.includes('ETIMEDOUT') ||
            apiError?.message?.includes('ECONNREFUSED') ||
            apiError?.message?.includes('ENOTFOUND') ||
            apiError?.code === 'ETIMEDOUT' ||
            apiError?.code === 'ECONNREFUSED';

          if (isConnectionError) {
            console.warn('[MEV] Connection failure on AUC first request - saving to offline queue');
            await this.saveToOfflineQueue(firstRequestData, job);

            job.status = 'completed';
            job.processedAt = new Date();
            job.result = {
              offline: true,
              message: 'AUC transaction saved to offline queue due to connection failure'
            };

            this.notifyListeners('job-completed', job);
            return;
          }

          throw apiError;
        }

        // Parse first response
        let firstResponseData;
        if (firstResponse && typeof firstResponse.data === 'string') {
          firstResponseData = JSON.parse(firstResponse.data);
        } else if (firstResponse && typeof firstResponse.data === 'object') {
          firstResponseData = firstResponse.data;
        } else {
          throw new Error('Received invalid or no data from first request');
        }

        if (firstResponseData && firstResponseData.retourTrans && firstResponseData.retourTrans.retourTransActu) {
          console.log('[MEV] First request completed successfully');

          // Update previous signature for second request
          mevStorage.setPreviousSignature(firstRequestData.reqTrans.transActu.signa.actu);
        } else {
          throw new Error('Invalid response format from first MEV request');
        }

        // Second request: Merchant copy with printing
        const secondRequestData = JSON.parse(JSON.stringify(formattedData));
        secondRequestData.reqTrans.transActu.formImpr = 'PAP';

        // Include REFS only if temporary bill was printed (check printHistory)
        const hasTemporaryBill = job.data.printHistory && job.data.printHistory.length > 0;
        if (hasTemporaryBill && job.data.printHistory && job.data.printHistory.length > 0) {
          // Use the same REFS logic as formatOrderData
          const lastPrintEvent = job.data.printHistory[job.data.printHistory.length - 1];
          secondRequestData.reqTrans.transActu.refs = [{
            noTrans: lastPrintEvent.transactionNumber,
            datTrans: lastPrintEvent.datTrans || this.formatDateTime(new Date(lastPrintEvent.timestamp)),
            avantTax: this.formatAmount(lastPrintEvent.avantTax)
          }];
          console.log('[MEV] Including REFS in second request (temporary bill exists):', secondRequestData.reqTrans.transActu.refs);
        } else {
          // Remove REFS if no temporary bill
          if (secondRequestData.reqTrans.transActu.refs) {
            delete secondRequestData.reqTrans.transActu.refs;
          }
          console.log('[MEV] No REFS in second request (no temporary bill)');
        }

        // Regenerate signature for second request
        const secondSignatureData = await this.generateTransactionSignature({
          noTrans: secondRequestData.reqTrans.transActu.noTrans,
          datTrans: secondRequestData.reqTrans.transActu.datTrans,
          tpsAmount: secondRequestData.reqTrans.transActu.mont.TPS,
          tvqAmount: secondRequestData.reqTrans.transActu.mont.TVQ,
          apresTax: secondRequestData.reqTrans.transActu.mont.apresTax,
          noTPS: transHeader['noTPS'],
          noTVQ: transHeader['noTVQ'],
          modImpr: 'PSP',
          modTrans: isUserInTrainingMode(aucCurrentUserId) ? 'FOR' : 'OPE',
          preced: this.getPreviousSignature()
        });
        secondRequestData.reqTrans.transActu.signa = secondSignatureData;

        // Update SIGNATRANSM for second request
        const secondActu = secondRequestData.reqTrans.transActu.signa.actu;
        const secondSignaTransmData = transHeader.authCode + transHeader.IDAPPRL + secondActu;
        const secondSignResult = await window.api.signData(secondSignaTransmData, activePrivateKey);
        if (!secondSignResult || !secondSignResult.success || !secondSignResult.signature) {
          throw new Error(`Failed to generate SIGNATRANSM for second request: ${secondSignResult?.error || 'IPC failure'}`);
        }
        const secondSignaTransm = normalizeSignature(secondSignResult.signature);

        const secondOptions = {
          ...options,
          headers: {
            ...options.headers,
            SIGNATRANSM: secondSignaTransm
          },
          body: JSON.stringify(secondRequestData)
        };

        console.log('[MEV] Sending second request (merchant copy with printing):', JSON.stringify(secondRequestData, null, 2));

        try {
          response = await window.api.performMutualTlsRequest(secondOptions);
          await logAPIResponse(
            secondOptions.url,
            'POST',
            secondRequestData,
            typeof response.data === 'string' ? JSON.parse(response.data) : response.data,
            100,
            200
          );
        } catch (apiError: any) {
          const isConnectionError = apiError?.message?.includes('timeout') ||
            apiError?.message?.includes('ETIMEDOUT') ||
            apiError?.message?.includes('ECONNREFUSED') ||
            apiError?.message?.includes('ENOTFOUND') ||
            apiError?.code === 'ETIMEDOUT' ||
            apiError?.code === 'ECONNREFUSED';

          if (isConnectionError) {
            console.warn('[MEV] Connection failure on AUC second request - saving to offline queue');
            await this.saveToOfflineQueue(secondRequestData, job);

            job.status = 'completed';
            job.processedAt = new Date();
            job.result = {
              offline: true,
              message: 'AUC transaction (second request) saved to offline queue due to connection failure'
            };

            this.notifyListeners('job-completed', job);
            return;
          }

          throw apiError;
        }

        // Use second request data as the formatted data for result
        formattedData.reqTrans.transActu = secondRequestData.reqTrans.transActu;
        formattedData.reqTrans.transActu.formImpr = 'PAP';
      } else {
        // Normal flow: single request
        try {
          response = await window.api.performMutualTlsRequest(options);
          await logAPIResponse(
            options.url,
            'POST',
            formattedData,
            typeof response.data === 'string' ? JSON.parse(response.data) : response.data,
            100,
            200
          );
        } catch (apiError: any) {
          // Check if this is a timeout or connection error
          const isConnectionError = apiError?.message?.includes('timeout') ||
            apiError?.message?.includes('ETIMEDOUT') ||
            apiError?.message?.includes('ECONNREFUSED') ||
            apiError?.message?.includes('ENOTFOUND') ||
            apiError?.code === 'ETIMEDOUT' ||
            apiError?.code === 'ECONNREFUSED';

          if (isConnectionError) {
            console.warn('[MEV] Connection failure detected - saving to offline queue');

            // Save to offline queue
            await this.saveToOfflineQueue(formattedData, job);

            // Mark job as completed (even though it's queued)
            // The receipt will print with "PROBLÈME DE COMMUNICATION"
            job.status = 'completed';
            job.processedAt = new Date();
            job.result = {
              offline: true,
              message: 'Transaction saved to offline queue due to connection failure'
            };

            this.notifyListeners('job-completed', job);
            return; // Exit early - don't throw error
          }

          // If not a connection error, rethrow
          throw apiError;
        }
      }

      // Log raw response
      console.log('MEV: API Response (Raw)', JSON.stringify(response, null, 2));

      // Parse and log the data field if it's a string
      let responseData;
      if (response && typeof response.data === 'string') {
        responseData = JSON.parse(response.data);
      } else if (response && typeof response.data === 'object') {
        responseData = response.data;
      } else {
        throw new Error('Received invalid or no data from server');
      }

      // Log parsed response data
      console.log('MEV: API Response (Parsed)', JSON.stringify(responseData, null, 2));

      if (responseData && responseData.retourTrans && responseData.retourTrans.retourTransActu) {
        const retourTransActu = responseData.retourTrans.retourTransActu;

        // Check for errors in the response and display them
        if (retourTransActu.listErr && retourTransActu.listErr.length > 0) {
          console.error('MEV: API returned errors', retourTransActu.listErr);

          // Display errors as persistent toast/alert with full details
          const errorMessages = retourTransActu.listErr
            .map((err: any) => {
              const parts = [];
              if (err.noTrans) parts.push(`Transaction: ${err.noTrans}`);
              if (err.id) parts.push(`ID: ${err.id}`);
              if (err.mess) parts.push(`Message: ${err.mess}`);
              if (err.codRetour) parts.push(`Code: ${err.codRetour}`);
              return parts.join(' | ');
            })
            .join('\n\n');

          // Log WEB-SRM errors to audit trail (only if not from batch transactions)
          // Note: According to SW-73.A-V, no error message is returned if error comes from batch
          // We check if this is a batch by looking for multiple transactions
          const isBatchTransaction = job.data?.order?.items && job.data.order.items.length > 1;

          if (!isBatchTransaction) {
            try {
              const { logOfflineError } = await import('../../utils/auditErrorLogger');
              const { logPOSEvent } = await import('../../utils/backgroundLogger');
              const sessionId = job.data?.sessionId || job.data?.session_id || job.data?.order?.session_id;

              for (const err of retourTransActu.listErr) {
                const errorDetails = {
                  noTrans: err.noTrans,
                  id: err.id,
                  codRetour: err.codRetour,
                  mess: err.mess
                };

                await logPOSEvent('WEB_SRM_ERROR', {
                  sessionId: sessionId || null,
                  ...errorDetails,
                  timestamp: Date.now()
                });

                if (sessionId) {
                  await logOfflineError(
                    sessionId,
                    `WEB-SRM Error: ${err.mess || 'Unknown error'} (ID: ${err.id || 'N/A'}, Code: ${err.codRetour || 'N/A'}, Transaction: ${err.noTrans || 'N/A'})`,
                    'WEB-SRM API Error'
                  );
                }
              }
            } catch (logError) {
              console.error('[MEV] Failed to log WEB-SRM error to audit trail:', logError);
              // Don't block error display if logging fails
            }
          }

          // Create a persistent error div at bottom-center
          const errorDiv = document.createElement('div');
          errorDiv.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            max-width: 600px;
            background: #f44336;
            color: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
            z-index: 10000;
            font-family: Arial, sans-serif;
          `;
          errorDiv.innerHTML = `
            <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 10px;">
              <strong style="font-size: 16px;">MEV API Error</strong>
              <button onclick="this.parentElement.parentElement.remove()" style="
                background: rgba(255,255,255,0.3);
                border: none;
                color: white;
                font-size: 20px;
                cursor: pointer;
                padding: 0 8px;
                border-radius: 4px;
              ">×</button>
            </div>
            <pre style="white-space: pre-wrap; margin: 0; font-size: 13px;">${errorMessages}</pre>
          `;
          document.body.appendChild(errorDiv);
        }

        const qrCodeUrl = await this.generateQRCodeUrl({
          ...(formattedData.reqTrans.transActu || {}),
          EMPRCERTIFTRANSM
        }, envirn);

        const settingsEnvrn = mevStorage.getSettingsEnvirn();
        let merchantCopyEnabled = false;
        let merchantResponseData = null;

        // For AUC (no payment) with PSP, merchant copy is always enabled (second request already sent)
        if (isAucNoPayment) {
          merchantCopyEnabled = true;
          // The second request response is already in responseData
          merchantResponseData = responseData;
        } else {
          const skipMerchantCopy = job.modImpr === 'ANN' || job.billType === 'cancellation';
          const merchantChoice = chosenMerchantCopy(job.data);

          if (!skipMerchantCopy && typeof merchantChoice === 'boolean') {
            merchantCopyEnabled = merchantChoice;
          } else if (settingsEnvrn && !skipMerchantCopy) {
            try {
              const parsedSettings = JSON.parse(settingsEnvrn);
              if (job.billType === 'temporary') {
                // Honor new temporary setting, fallback to legacy merchantCopy
                merchantCopyEnabled = parsedSettings.merchantCopyTemporary === true || (parsedSettings.merchantCopyTemporary === undefined && parsedSettings.merchantCopy === true);
              } else {
                // Closing receipt (including refunds) uses the closing setting
                merchantCopyEnabled = parsedSettings.merchantCopyClosing === true;
              }
            } catch (e) { }
          }

          if (merchantCopyEnabled) {
            const duplicateData = JSON.parse(JSON.stringify(formattedData));
            duplicateData.reqTrans.transActu.modImpr = 'DUP';
            const duplicateResponse = await window.api.performMutualTlsRequest({
              ...options,
              body: JSON.stringify(duplicateData),
              timeout: getApiWaitTime() // Apply configured wait time (in milliseconds)
            });
            await logAPIResponse(
              options.url,
              'POST',
              duplicateData,
              typeof duplicateResponse.data === 'string' ? JSON.parse(duplicateResponse.data) : duplicateResponse.data,
              100,
              200
            );
            if (duplicateResponse && duplicateResponse.data) {
              merchantResponseData = typeof duplicateResponse.data === 'string' ? JSON.parse(duplicateResponse.data) : duplicateResponse.data;
            }
          }
        }

        job.result = {
          psiNoTrans: responseData.retourTrans.retourTransActu.psiNoTrans,
          psiDatTrans: responseData.retourTrans.retourTransActu.psiDatTrans,
          responseData: {
            ...formattedData,
            retourTrans: responseData.retourTrans
          },
          merchantResponseData: merchantResponseData ? {
            reqTrans: {
              ...formattedData.reqTrans,
              transActu: {
                ...formattedData.reqTrans.transActu,
                modImpr: isAucNoPayment ? 'PSP' : 'DUP'  // Use PSP for AUC, DUP for normal merchant copy
              }
            },
            retourTrans: merchantResponseData.retourTrans
          } : null,
          qrCodeUrl,
          qrCodeSize: 440,
          originalOrderData: job.data,
          merchantCopyEnabled
        };

        // SUCCESS: If we bundled offline transactions, mark them as sent and exit offline mode
        if (queueCount > 0 && formattedData.reqTrans.transLot) {
          console.log(`[MEV] Batch sync successful - marking ${queueCount} transactions as sent`);

          try {
            const unsentTransactions = await mevOfflineQueue.getUnsentTransactions();

            for (const transaction of unsentTransactions) {
              if (transaction.id) {
                await mevOfflineQueue.markTransactionSent(transaction.id);
              }
            }

            mevOfflineQueue.exitOfflineMode();
            console.log('[MEV] All queued transactions successfully sent and removed from queue');
          } catch (cleanupError) {
            console.error('[MEV] Error cleaning up offline queue:', cleanupError);
          }
        }

        job.status = 'completed';
        job.processedAt = new Date();
        this.notifyListeners('job-processed', job);
      } else {
        throw new Error('Invalid response format from MEV endpoint');
      }
    } catch (error) {
      job.status = 'failed';
      job.processedAt = new Date();
      job.error = error instanceof Error ? error.message : String(error);

      // Log API failure to system log
      try {
        const envirn = mevStorage.getEnvirn();
        const url = envirn === 'PROD' ? TRANSACTION_ENDPOINT_PROD : TRANSACTION_ENDPOINT;
        const requestData = (job as any).formattedData || job.data || { note: 'Request data unavailable' };

        await logAPIResponse(
          url,
          'POST',
          requestData,
          { error: job.error },
          0,
          500
        );
      } catch (logErr) {
        console.error('[MEV] Failed to log API response:', logErr);
      }

      // Log transmission error to audit trail (only if not from batch transactions)
      try {
        const { logOfflineError } = await import('../../utils/auditErrorLogger');
        const sessionId = job.data?.sessionId || job.data?.session_id || job.data?.order?.session_id;

        if (sessionId) {
          // Check if this is a batch transaction
          const isBatchTransaction = job.data?.order?.items && job.data.order.items.length > 1;

          // According to SW-73.A-V: no error message is returned if error comes from batch
          // But we still log it for internal tracking
          await logOfflineError(
            sessionId,
            error instanceof Error ? error : new Error(String(error)),
            `Electronic Bill Transmission Error: ${error instanceof Error ? error.message : String(error)}${isBatchTransaction ? ' (Batch Transaction)' : ''}`
          );
        }
        const { logPOSEvent } = await import('../../utils/backgroundLogger');
        await logPOSEvent('WEB_SRM_ERROR', {
          sessionId: sessionId || null,
          id: '',
          codRetour: '',
          mess: error instanceof Error ? error.message : String(error),
          timestamp: Date.now()
        });
      } catch (logError) {
        console.error('[MEV] Failed to log transmission error to audit trail:', logError);
        // Don't block error handling if logging fails
      }

      this.notifyListeners('job-failed', job);
    }
  }

  /**
   * Format order data according to MEV requirements
   */
  private async formatOrderData(orderData: OrderData, billType?: 'temporary' | 'final' | 'cancellation', forceModImpr?: string): Promise<any> {
    if (!orderData?.order) return { reqTrans: { transActu: {} } };

    console.log('MEV: ORDERDATA', orderData);

    const order = orderData.order;
    const transHeader = JSON.parse(mevStorage.getTransactionHeader() || '{}');
    let storedActivity = '';
    try {
      const storeId = mevStorage.getStoreId() || '';
      storedActivity = storeId ? localStorage.getItem(`billingSubactivity_${storeId}`) || '' : '';
    } catch {
      storedActivity = '';
    }
    const activityCode = storeBillingSubactivity({ billingSubactivity: storedActivity });
    const sectorCode = this.getSectorCode();

    // Get actual POS user name - prioritize value from OrderData
    let currentUserName = orderData.user_name || 'POS User';
    let currentUserId: string | null = null;

    // If not in OrderData, try storage (fallback)
    if (currentUserName === 'POS User') {
      try {
        const envirn = mevStorage.getEnvirn();
        const storeId = mevStorage.getStoreId();

        // Try POSUserContext storage first (currentPOSUser_storeId)
        if (storeId) {
          const posUserData = localStorage.getItem(`currentPOSUser_${storeId}`);
          if (posUserData) {
            const posUser = JSON.parse(posUserData);
            currentUserName = posUser.name || 'POS User';
            currentUserId = posUser.id || null;
            console.log('MEV: POS User Name (from POSUserContext)', { currentUserName, posUser });
          }
        }

        // Fallback to old pos_user storage
        if (currentUserName === 'POS User') {
          const userData = JSON.parse(mevStorage.getPOSUser('') || '{}');
          currentUserName = userData.name || userData.displayName || userData.username || 'POS User';
          currentUserId = userData.id || null;
          console.log('MEV: POS User Name (from fallback)', { currentUserName, userData });
        }
      } catch (e) {
        console.error('MEV: Failed to get POS user name:', e);
      }
    } else {
      console.log('MEV: POS User Name (from OrderData)', { currentUserName });
    }

    let companyInfo = JSON.parse(mevStorage.getCompanyInfo() || '{}');
    try {
      const storeId = mevStorage.getStoreId() || '';
      const envirn = mevStorage.getEnvirn();
      if (storeId) {
        const status = await getCloudMevStoreStatus(storeId, envirn).catch(() => null);
        const business = status?.snapshot?.business || {};
        if (String(business.Name || '').trim()) {
          companyInfo = {
            Name: String(business.Name || '').trim(),
            email: String(business.email || '').trim(),
            phone: String(business.phone || '').trim(),
            address_1: String(business.address_1 || '').trim(),
            address_2: String(business.address_2 || '').trim(),
            city: String(business.city || '').trim(),
            Province: String(business.Province || '').trim(),
            postcode: String(business.postcode || '').trim(),
            country: String(business.country || '').trim(),
          };
          mevStorage.setCompanyInfo(JSON.stringify(companyInfo));
        }
      }
    } catch (e) {
      console.error('MEV: certificate business was not loaded', e);
    }

    // Extract civic number from address (skip unit numbers)
    // Examples: "123-456, rue Unetelle" -> "456", "466 Main St" -> "466"
    let docNoCiviq = '';
    if (companyInfo.address_1) {
      const address = String(companyInfo.address_1);
      // Try multiple patterns:
      // 1. "123-456, rue" -> "456" (unit-civic with comma)
      // 2. "123-456 rue" -> "456" (unit-civic with space)
      // 3. "456 Main" -> "456" (civic only)
      const patterns = [
        /\d+-(\d+)[,\s]/,  // Match: 123-456, or 123-456 
        /^(\d+)\s/,         // Match: 456 Main
        /(\d+)/             // Fallback: any number
      ];

      for (const pattern of patterns) {
        const match = address.match(pattern);
        if (match && match[1]) {
          docNoCiviq = match[1];
          break;
        }
      }
    }
    console.log('MEV: Civic Number Extraction', {
      address: companyInfo.address_1,
      extracted: docNoCiviq
    });

    const docCp = companyInfo.postcode?.replace(/\s+/g, '') || '';

    // Sanitize product names - keep only English/French characters (including accents), numbers, spaces, hyphens, apostrophes
    const sanitizeProductName = (name: string): string => {
      return name
        .replace(/[^a-zA-Z0-9\s\u00C0-\u00FF\u0152\u0153\u0178'-]/g, '') // Keep English, French (with accents), numbers, spaces, hyphens, apostrophes
        .replace(/\s+/g, ' ')     // Normalize whitespace
        .trim();
    };

    // PERFORMANCE OPTIMIZATION: Create a Set of item IDs that have alcohol tax applied
    // This is done once, then we use fast O(1) lookups instead of O(n) array searches for each item
    const alcoholTaxableItemIds = (() => {
      try {
        const taxes = orderData.storeTaxes || [];
        console.log('[MEV ALCOHOL TAX] Building alcohol tax lookup from orderData.storeTaxes');

        // Find the Quebec-alcohol tax in the store's tax configuration
        const alcoholTax = taxes.find((tax: any) =>
          tax.taxName?.toLowerCase() === 'quebec-alcohol' ||
          tax.taxName?.toLowerCase() === 'tax s'
        );

        if (!alcoholTax) {
          console.log('[MEV ALCOHOL TAX] No Quebec-alcohol tax found');
          return new Set<string>();
        }

        console.log('[MEV ALCOHOL TAX] Found alcohol tax:', {
          name: alcoholTax.taxName,
          isSpecific: alcoholTax.isSpecific,
          itemCount: alcoholTax.checkedItems?.length
        });

        // The selected products are the alcohol list, even when the specific-items flag was not saved.
        if (alcoholTax.checkedItems && Array.isArray(alcoholTax.checkedItems)) {
          const itemIds = new Set(alcoholTax.checkedItems.map((item: any) => item.id).filter(Boolean));
          console.log('[MEV ALCOHOL TAX] Created lookup Set with', itemIds.size, 'items:', Array.from(itemIds));
          return itemIds;
        }

        return new Set<string>();
      } catch (error) {
        console.error('[MEV ALCOHOL TAX] Error building lookup:', error);
        return new Set<string>();
      }
    })();

    // Helper function to check if an item has the Quebec-alcohol tax applied
    const hasAlcoholTax = (item: any): boolean => {
      const keys = [item?.sku, item?.id].filter(Boolean);
      const isTaxable = keys.some((key) => alcoholTaxableItemIds.has(key));
      console.log('[MEV ALCOHOL TAX] Item:', item.name, 'SKU:', item.sku, 'Taxable:', isTaxable);
      return isTaxable;
    };


    // Format line items for MEV
    const habPartyIds = new Set<string>();
    let habPartyMissing = false;
    const lineItems = (() => {
      // Special handling for cancellation transactions
      if (billType === 'cancellation') {
        return [{
          qte: '+00001.00',  // Always 1 for SOB
          descr: 'SOB',
          prix: '+000000000.00',
          tax: 'SOB',
          acti: 'SOB'
        }];
      }

      // Normal item formatting for non-cancellation transactions
      if (!order.items || order.items.length === 0) {
        return [{
          qte: '+00001.00',
          descr: 'No items',
          prix: '+000000000.00',
          tax: 'FP',
          acti: lineItemActi(activityCode, '', 0)
        }];
      }

      const formattedItems = order.items.map((item: any) => {
        // CRITICAL: Check if this is the Annulation line (for cancellation after print)
        const isAnnulationLine = item.sku === 'ANNULATION' || item.name === 'Annulation';

        if (isAnnulationLine) {
          console.log('[MEV] Formatting Annulation item - preserving acti and preci');
          // Return Annulation item with preserved fields
          const qty = item.qty || 1;
          const qtyFixed = Math.abs(Number(qty)).toFixed(2);
          const [wholePart, decimalPart] = qtyFixed.split('.');
          const sign = Number(qty) < 0 ? '-' : '+';
          const formattedQty = `${sign}${wholePart.padStart(5, '0')}.${decimalPart}`;

          const unitPrice = Math.round((Number(item.price) || 0) * 100) / 100;
          const roundedQty = (Number(qty) < 0 ? -1 : 1) * Number(Math.abs(Number(qty)).toFixed(2));
          const lineTotal = Math.round(roundedQty * unitPrice * 100) / 100;
          return {
            qte: formattedQty,
            descr: item.name || 'Annulation',
            prix: this.formatAmount(lineTotal),
            unitr: this.formatAmount(unitPrice),
            tax: LINE_TAX_CODES.includes(String(item.tax || '').toUpperCase()) ? String(item.tax).toUpperCase() : 'FP',
            acti: lineItemActi(activityCode, '', unitPrice),
            preci: item.preci || [{
              descr: 'commande annulée',
              acti: 'SOB'  // preci can use SOB
            }]
          };
        }

        // Regular item formatting
        // Use tax-based detection instead of SKU-based
        const taxCode = codeForLine(item, orderData.storeTaxes || [], hasAlcoholTax(item));

        // Format quantity to handle decimals correctly: +XXXXX.DD
        // Split into whole and decimal parts
        const qty = item.qty || 1;
        const qtyFixed = Math.abs(Number(qty)).toFixed(2); // Ensure 2 decimal places
        const [wholePart, decimalPart] = qtyFixed.split('.');
        const sign = Number(qty) < 0 ? '-' : '+';
        const formattedQty = `${sign}${wholePart.padStart(5, '0')}.${decimalPart}`;

        const unitPrice = Math.round((Number(item.price) || 0) * 100) / 100;
        const roundedQty = (Number(qty) < 0 ? -1 : 1) * Number(Math.abs(Number(qty)).toFixed(2));
        const lineTotal = Math.round(roundedQty * unitPrice * 100) / 100;
        const tagged = itemFiscalActivity(item);
        const acti = lineItemActi(activityCode, tagged, unitPrice);
        if (acti === 'HAB') {
          if (item.thirdPartyId) habPartyIds.add(String(item.thirdPartyId));
          else habPartyMissing = true;
        }
        return {
          qte: formattedQty,
          descr: sanitizeProductName(item.name || '').substring(0, 50),
          prix: this.formatAmount(lineTotal),
          unitr: this.formatAmount(unitPrice),
          tax: taxCode,
          acti
        };
      });

      // Check if there are any alcohol items (FPS)
      const hasAlcoholItemsInList = formattedItems.some(item => String(item.tax || '').includes('S') && item.tax !== 'SOB');

      // If there are alcohol items, add the disclosure once at the end
      if (hasAlcoholItemsInList) {
        formattedItems.push({
          qte: '+00000.00',
          descr: 'Taxe(s) supplémentaire(s)',
          prix: '+000000000.00',
          tax: 'SOB',
          acti: 'SOB',
          preci: [
            {
              qte: '+00000.00',
              descr: 'Boiss. alc. (t. incl.)',
              prix: '+000000000.00',
              tax: 'SOB',
              acti: 'SOB'
            }
          ]
        } as any);
      }
      return formattedItems;
    })();

    let clint: ReturnType<typeof clintFromParty> | null = null;
    if (habPartyMissing || habPartyIds.size > 1) {
      throw new Error(habPartyIds.size > 1
        ? 'These services belong to different companies and must be on separate bills.'
        : 'The frequent third party record is missing.');
    }
    if (habPartyIds.size === 1) {
      const partyStoreId = mevStorage.getStoreId() || '';
      const party = partyStoreId ? await loadFrequentThirdParty(partyStoreId, Array.from(habPartyIds)[0]) : null;
      if (!party) throw new Error('The frequent third party record is missing.');
      clint = clintFromParty(party);
    }

    const typedTaxes = (order.tax_details || []).filter((t) => t.fiscalType);
    const sumType = (type: string, label: string) => typedTaxes.length > 0
      ? typedTaxes.filter((t) => t.fiscalType === type).reduce((s, t) => s + (Number(t.total) || 0), 0)
      : order.tax_details?.find((t) => t.label.toUpperCase() === label)?.total || 0;
    const tps = sumType('F', 'TPS');
    const tvq = sumType('P', 'TVQ');

    const now = new Date();
    const transactionDate = this.formatDateTime(now);
    const timezone = transHeader.timezone || '-05:00';
    const utc = this.isDST(now, timezone);

    const payloadTransactionNumber = (() => {
      // PRIORITY: Use order_id which already has suffix from convertCartToMev
      const transactionNumber = String(order.order_id);

      console.log('MEV: Transaction Number for API', {
        order_id: order.order_id,
        transactionNumber,
        isRevised: orderData.isRevised,
        printVersionSuffix: orderData.printVersionSuffix,
        printHistory: orderData.printHistory,
        replacesCount: orderData.replacesCount
      });

      return transactionNumber;
    })();
    // Determine payment method first
    const mappedPayment = billType === 'cancellation' ? 'SOB' : (billType === 'temporary' ? 'SOB' : this.getPaymentMethod(orderData));
    const paymentMethod = billType === 'final' ? (onlineModPai(orderData, mappedPayment) || mappedPayment) : mappedPayment;

    // If payment method is AUC (no payment), automatically set modImpr to PSP
    let modImprValue = forceModImpr || orderData.modImpr || 'FAC';
    if (paymentMethod === 'AUC' && !forceModImpr) {
      modImprValue = 'PSP';
      console.log('[MEV] AUC payment detected - setting modImpr to PSP');
    }
    console.log('Back to station', 'print mode', {
      billType,
      modImpr: modImprValue,
      typTrans: billType === 'temporary' ? 'ADDI' : 'RFER',
    });
    const lastHistoryEntry = orderData.printHistory && orderData.printHistory.length > 0
      ? orderData.printHistory[orderData.printHistory.length - 1]
      : null;
    const priorEntry = priorBillHistoryEntry(orderData.printHistory, payloadTransactionNumber);
    const historyRefFrom = (entry: any) => (entry?.transactionNumber
      ? [{
        noTrans: channelReferenceNumber(payloadTransactionNumber, entry.transactionNumber) || entry.transactionNumber,
        datTrans: /^\d{14}$/.test(String(entry.datTrans || ''))
          ? String(entry.datTrans)
          : this.formatDateTime(new Date(entry.timestamp || Date.now())),
        avantTax: this.formatAmount(entry.avantTax),
      }]
      : null);
    const historyRefs = orderData.refs && orderData.refs.length > 0
      ? orderData.refs.map((ref: any) => ({
        ...ref,
        noTrans: channelReferenceNumber(payloadTransactionNumber, ref?.noTrans) || ref?.noTrans,
        avantTax: this.formatAmount(ref?.avantTax),
      }))
      : (modImprValue === 'RPR'
        ? historyRefFrom(lastHistoryEntry)
        : (orderData.isRevised ? historyRefFrom(priorEntry) : null));
    const billRefs = modImprValue === 'RPR'
      ? (historyRefs || [{
        noTrans: payloadTransactionNumber,
        datTrans: transactionDate,
        avantTax: this.formatAmount(order.sub_total),
      }])
      : historyRefs;

    const coveredActivities = lineItems
      .map((line: { acti?: string }) => line?.acti)
      .filter((acti): acti is string => Boolean(acti) && acti !== 'SOB');
    const foodTruckOnly = coveredActivities.length > 0 && coveredActivities.every((acti) => acti === 'CDR');

    return {
      reqTrans: {
        transActu: {
          sectActi: (() => {
            const serviceType = determineServiceType(orderData);
            return {
              abrvt: sectorCode,
              typServ: serviceType,
              noTabl: getTableNumber(orderData, serviceType),
              nbClint: getClientNumber(orderData)
            };
          })(),
          noTrans: payloadTransactionNumber,
          nomMandt: companyInfo.Name || '',
          ...(foodTruckOnly ? {} : { docAdr: { docNoCiviq, docCp } }),
          nomUtil: currentUserName,
          relaCommer: 'B2C',
          ...(clint ? { clint } : {}),
          datTrans: transactionDate,
          utc: utc,
          items: lineItems,
          mont: {
            avantTax: this.formatAmount(order.sub_total),
            TPS: this.formatAmount(tps),
            TVQ: this.formatAmount(tvq),
            apresTax: this.formatAmount(order.total),
            ...(orderData.billAdjustment ? { ajus: this.formatAmount(orderData.billAdjustment) } : {}),
          },
          noDossFO: transHeader['noDossFO'],
          noTax: { noTPS: transHeader['noTPS'], noTVQ: transHeader['noTVQ'] },
          typTrans: billType === 'cancellation' ? 'RFER' : (billType === 'temporary' ? 'ADDI' : 'RFER'),
          commerElectr: billType === 'final' && paymentMethod !== 'SOB' && paymentMethod !== 'PAC'
            ? commerElectrFor(orderData)
            : 'N',
          modPai: paymentMethod,
          modImpr: modImprValue,
          // Reverse transactions do not print. A reproduction reprints the same bill.
          formImpr: orderData.formImpr === 'NON' || orderData.isReverse
            ? 'NON'
            : (billType === 'cancellation' ? 'NON' : resolveFormImpr(orderData, { modImpr: modImprValue, billType })),
          modTrans: isUserInTrainingMode(currentUserId) ? 'FOR' : 'OPE',
          ...(billRefs ? { refs: billRefs } : {}),
          SEV: {
            idSEV: transHeader['IDSEV'],
            idVersi: transHeader['IDVERSI'],
            codCertif: transHeader['CODCERTIF'],
            idPartn: transHeader['IDPARTN'],
            versi: transHeader['VERSI'],
            versiParn: transHeader['VERSIPARN']
          }
        }
      }
    };
  }

  /**
   * Mapping POS payment codes to MEV codes
   */
  private getPaymentMethod(orderData: OrderData): string {
    const code = orderData.order?.payment_method?.code?.toUpperCase() || 'SOB';
    switch (code) {
      case 'CASH': return 'ARG';
      case 'CREDIT': return 'CRE';
      case 'DEBIT': return 'DEB';
      default:
        return (this.MEV_PAYMENT_METHODS as any).includes(code) ? code : 'SOB';
    }
  }

  /**
   * Generate transaction signature via IPC
   */
  private async generateTransactionSignature(data: any): Promise<any> {
    const dataToSign = data.noTrans + data.datTrans + data.tpsAmount + data.tvqAmount + data.apresTax + data.noTPS + data.noTVQ + data.modImpr + data.modTrans + data.preced;
    console.log('MEV: Signature Input Data', {
      noTrans: data.noTrans,
      datTrans: data.datTrans,
      tpsAmount: data.tpsAmount,
      tvqAmount: data.tvqAmount,
      apresTax: data.apresTax,
      noTPS: data.noTPS,
      noTVQ: data.noTVQ,
      modImpr: data.modImpr,
      modTrans: data.modTrans,
      preced: data.preced,
      concatenated: dataToSign
    });

    const envirn = mevStorage.getEnvirn() || 'DEV';
    const storeId = mevStorage.getStoreId() || '';
    console.log('[build debug] generateTransactionSignature - storeId:', storeId);
    if (!storeId) throw new Error('Store ID not found');

    await mevCertService.getActiveCertThumbprint(storeId, envirn);
    const privateKey = await getPrivateKey(storeId, envirn.toLowerCase());
    if (!privateKey) throw new Error('Private key not found');

    const signResult = await window.api.signData(dataToSign, privateKey);
    if (!signResult || !signResult.success || !signResult.signature) throw new Error('Signature failed');

    console.log('MEV: Generated Signature', {
      actu: signResult.signature,
      actuLength: signResult.signature.length,
      preced: data.preced,
      precedLength: data.preced.length
    });

    // MEV requires signatures to be exactly 88 characters
    const normalizeSignature = (sig: string): string => {
      if (sig.length === 88) return sig;
      if (sig.length > 88) return sig.substring(0, 88);
      return sig.padEnd(88, '=');
    };

    const normalizedActu = normalizeSignature(signResult.signature);
    const normalizedPreced = normalizeSignature(data.preced);

    console.log('MEV: Normalized Signatures', {
      actu: normalizedActu,
      actuLength: normalizedActu.length,
      preced: normalizedPreced,
      precedLength: normalizedPreced.length
    });

    mevStorage.setPreviousSignature(normalizedActu);
    return { datActu: data.datTrans, actu: normalizedActu, preced: normalizedPreced };
  }

  private getPreviousSignature(): string {
    return mevStorage.getPreviousSignature();
  }

  private formatAmount(amount: number | string | undefined): string {
    if (amount === undefined || amount === null) return '+000000000.00';
    let num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : amount;
    if (isNaN(num)) return '+000000000.00';
    const sign = num < 0 ? '-' : '+';
    const absNum = Math.abs(num);
    const parts = absNum.toFixed(2).split('.');
    return `${sign}${parts[0].padStart(9, '0')}.${parts[1]}`;
  }

  private getSectorCode(): string { return 'RBC'; }

  private getEnvKey(prefix: string, envirn: string | null): string {
    return `${prefix}_${(envirn || 'dev').toLowerCase()}`;
  }

  private formatDateTime(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  }

  private isDST(now: Date, offset: string): string {
    const fixed = permanentUtc(now, offset);
    if (fixed) return fixed;

    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    const minutes = now.getHours() * 60 + now.getMinutes();
    const march1 = new Date(year, 2, 1);
    const startDay = 1 + ((7 - march1.getDay()) % 7) + 7;
    const nov1 = new Date(year, 10, 1);
    const endDay = 1 + ((7 - nov1.getDay()) % 7);

    if (month < 2 || (month === 2 && day < startDay) || month > 10 || (month === 10 && day > endDay)) {
      return offset + 'N';
    }
    if (month === 2 && day === startDay) {
      return minutes < 120 ? offset + 'N' : offset + 'A';
    }
    if (month === 10 && day === endDay) {
      return minutes < 120 ? offset + 'A' : offset + 'N';
    }
    return offset + 'A';
  }

  async generateQRCodeUrl(transactionDataForQR: any, envirn: string): Promise<string> {
    try {
      const lowerEnv = envirn.toLowerCase();
      const storeId = String(
        transactionDataForQR.store_id
        || transactionDataForQR.storeId
        || mevStorage.getStoreId()
        || ''
      ).trim();

      // Get certifPSI from MEMORY CACHE
      let certifPSI = await certificateMemoryCache.getCertifPSI(storeId, lowerEnv);

      if (!certifPSI) {
        console.warn('[QR Code] ⚠️  certifPSI not in cache, attempting to load from Windows Store...');

        // Try to load all certificates (including PSI)
        try {
          await certificateMemoryCache.loadPrivateKey(storeId, lowerEnv);
          certifPSI = await certificateMemoryCache.getCertifPSI(storeId, lowerEnv);
        } catch (err) {
          console.error('[QR Code] Failed to load certificates:', err);
        }
      }

      if (!certifPSI) {
        console.error('[QR Code] ❌ CRITICAL: certifPSI not available - QR code will contain UNENCRYPTED data!');
        console.error('[QR Code] Please ensure certificate was added successfully with certifPSI from MEV API');
      } else {
        console.log('[QR Code] ✓ Using cached certifPSI from memory (instant access, no I/O)');
      }

      const emprCertifSEV = (transactionDataForQR.EMPRCERTIFTRANSM || '').padEnd(40, ' ');
      const datTrans = (transactionDataForQR.datTrans || '').padEnd(14, ' ');
      const TPS = this.formatAmount(transactionDataForQR.mont?.TPS || 0).padEnd(13, ' ');
      const TVQ = this.formatAmount(transactionDataForQR.mont?.TVQ || 0).padEnd(13, ' ');
      const apresTax = this.formatAmount(transactionDataForQR.mont?.apresTax || 0).padEnd(13, ' ');
      const mtdu = this.formatAmount(transactionDataForQR.mtdu ?? transactionDataForQR.mont?.apresTax ?? 0).padEnd(13, ' ');
      const noTPS = (transactionDataForQR.noTax?.noTPS || '').padEnd(15, ' ');
      const noTVQ = (transactionDataForQR.noTax?.noTVQ || '').padEnd(16, ' ');
      const modImpr = (transactionDataForQR.modImpr || 'FAC').padEnd(3, ' ');
      const modTrans = (transactionDataForQR.modTrans || 'OPE').padEnd(3, ' ');
      const actu = (transactionDataForQR.signa?.actu || '').replace(/=+$/, '').substring(0, 88).padEnd(88, '=');
      const preced = (transactionDataForQR.signa?.preced || '').replace(/=+$/, '').substring(0, 88).padEnd(88, '=');
      const origNoTrans = transactionDataForQR.noTrans || '';
      const noTrans = '='.repeat(Math.max(0, 10 - origNoTrans.length)) + origNoTrans.substring(0, 10);

      // Concatenate data according to MEV specification (353 characters before encryption)
      const concatenatedData = emprCertifSEV + datTrans + TPS + TVQ + apresTax + mtdu + noTPS + noTVQ + modImpr + modTrans + actu + preced + noTrans;
      console.log('[QR Code] Concatenated data length:', concatenatedData.length);

      // Encrypt with PSI public key (required by MEV specification)
      let fValue = concatenatedData;
      if (certifPSI) {
        console.log('[QR Code] Encrypting data with PSI public key...');
        const result = await window.api.encryptQrDataWithOpenSSL(concatenatedData, certifPSI);
        if (result.success && result.data) {
          fValue = result.data;
          console.log('[QR Code] ✓ Data encrypted successfully, length:', fValue.length);
        } else {
          console.error('[QR Code] ❌ Encryption failed:', result.error);
        }
      } else {
        console.error('[QR Code] ❌ CRITICAL: QR code will contain UNENCRYPTED data (certifPSI missing)');
      }

      const baseUrl = envirn === 'PROD' ? 'https://qr.mev-web.ca?f=' : 'https://cnfr.qr.mev-web.ca?f=';
      const finalUrl = baseUrl + encodeURIComponent(fValue);
      console.log('[QR Code] Final URL length:', finalUrl.length);

      return finalUrl;
    } catch (error) {
      console.error('[QR Code] Error generating QR code:', error);
      return '';
    }
  }

  public async processTemporaryBill(cartItems: any[], store: any, totals: { total: number; totalWithTaxes: number; taxes: any[] }, session?: any, storeId?: string): Promise<any> {
    // Store ID should be passed from the caller (from StoreContext)
    // This is more reliable than reading from localStorage
    if (!storeId) {
      storeId = store?.id || store?.storeId;
    }

    if (!storeId) {
      throw new Error('Store ID is required for MEV operations. Please ensure a store is selected.');
    }

    const orderData = this.convertCartToMev(cartItems, store, totals, session);
    const job: OrderJob = {
      id: `temp_${Date.now()}`,
      data: { ...orderData, store_id: storeId }, // Pass store ID directly in job data
      status: 'pending',
      createdAt: new Date(),
      billType: 'temporary'
    };
    this.jobs.push(job);
    await this.processJob(job);
    if (job.status === 'completed' && job.result) return job.result;
    if (job.result?.cloudMevOverlay) return job.result;
    throw new Error(job.error || 'Failed to process temporary bill');
  }

  /**
   * Process a cancellation transaction (modImpr: 'ANN')
   * Creates a MEV cancellation record with zero amounts while preserving audit trail
   * 
   * MEV Cancellation Format:
   * - typTrans: "RFER" (closing record; SOB is a payment mode, not a transaction type)
   * - modPai: "SOB"
   * - modImpr: "ANN" (Annulation)
   * - modTrans: "OPE" (Operation, NOT "ANN")
   * - formImpr: "NON" (No printing)
   * - items: Single SOB item with zero amounts
   */
  public async processCancellation(canceledOrder: any, session?: any): Promise<any> {
    console.log('[MEV CANCELLATION] Processing cancellation transaction', {
      posOrderId: canceledOrder.posOrderId,
      sessionId: session?.id,
      originalItems: canceledOrder.originalItems?.length
    });

    try {
      // CRITICAL: Use channel order ID (session ID), NOT POS order ID
      const channelOrderId = session?.id || session?.tableName || session?.sessionId || canceledOrder.sessionId || 'CANCELED';

      console.log('[MEV CANCELLATION] Using channel order ID:', {
        channelOrderId,
        posOrderId: canceledOrder.posOrderId,
        sessionId: session?.id,
        tableName: session?.tableName
      });

      // Build cancellation order data with MEV-specific SOB format
      const cancellationOrderData: OrderData = {
        order: {
          order_id: channelOrderId,  // Use channel order ID (e.g., "CS010", "T5", "DL001")
          // MEV Cancellation: Always use single SOB item with zero amounts
          tax_details: [],
          items: [
            {
              qty: 1,  // Always 1 for SOB
              name: 'SOB',  // Sans Objet
              price: 0,
              sku: 'SOB'
            }
          ],
          sub_total: 0,
          tax_amount: 0,
          total: 0,
          payment_method: { code: 'SOB' },  // Sans Objet payment
          note: `Canceled by ${canceledOrder.canceledBy || 'User'}`,
          order_channel: session?.orderChannel,
          table_name: session?.tableName,
          guest_count: session?.guests || session?.guestCount,
          session_id: session?.id || session?.sessionId
        },
        note: `Order Canceled: ${channelOrderId}`,
        session_id: session?.id || session?.sessionId,
        user_name: canceledOrder.canceledBy || session?.user?.name || session?.user?.displayName,
        auditLog: canceledOrder.auditLog || [],
        printHistory: canceledOrder.printHistory || [],
        printVersionSuffix: 0,
        isRevised: false,
        replacesCount: 0,
        modImpr: 'ANN',  // Annulation (Cancellation)
        storeTaxes: []
      };

      // Create MEV job for cancellation with special billType
      const job: OrderJob = {
        id: `cancel_${Date.now()}`,
        data: cancellationOrderData,
        status: 'pending',
        createdAt: new Date(),
        billType: 'cancellation' as any,  // Special type for cancellation
        modImpr: 'ANN'  // Force cancellation mode
      };

      this.jobs.push(job);
      await this.processJob(job);

      if (job.status === 'completed' && job.result) {
        console.log('[MEV CANCELLATION] Cancellation transaction completed successfully');
        return job.result;
      }
      if (job.result?.cloudMevOverlay) return job.result;

      throw new Error(job.error || 'Failed to process cancellation');
    } catch (error) {
      console.error('[MEV CANCELLATION] Error processing cancellation:', error);
      throw error;
    }
  }

  /**
   * Process a cancellation transaction AFTER temporary bill was printed
   * Creates a MEV RFER transaction with printed receipt showing "FACTURE ANNULÉE"
   */
  public async processCancellationAfterPrint(
    canceledOrder: any,
    session?: any,
    originalTransactionNumber?: string,
    originalOrderData?: any
  ): Promise<any> {
    console.log('[MEV CANCELLATION AFTER PRINT] Processing', {
      canceledPosOrderId: canceledOrder.posOrderId,
      originalTransactionNumber
    });

    try {
      // Format date for MEV
      const formatDateForMEV = (date: any): string => {
        const d = date?.toDate ? date.toDate() : (date instanceof Date ? date : new Date(date));
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}${month}${day}${hours}${minutes}${seconds}`;
      };

      // Build cancellation order data with RFER format
      // CRITICAL: Use channel order ID (session ID) with suffix, NOT POS order ID
      const channelOrderId = session?.id || session?.sessionId || canceledOrder.sessionId;
      const transactionNumber = `${channelOrderId}-${canceledOrder.printVersionSuffix}`;

      const cancellationOrderData: OrderData = {
        order: {
          order_id: transactionNumber, // e.g., "CS010-1" NOT "BAABC010-1"
          tax_details: [],
          items: [
            // Step 1: Original items with positive quantities (Step 2 removed - no longer duplicate with negatives)
            ...(canceledOrder.items || []).map((item: any) => ({
              qty: item.qty || item.amount || 1,
              name: item.name || item.printName || '',
              price: item.price || 0,
              sku: item.id || '',
              ...copyFiscalTags(item),
            })),

            // Step 2: Annulation line with NEGATIVE amount (offset of total before taxes)
            // Calculate by summing all items directly to ensure accuracy
            {
              qty: 1,
              name: 'Annulation',
              // Calculate total of all items (before taxes)
              price: -(canceledOrder.items || []).reduce((sum: number, item: any) => {
                const qty = item.qty || item.amount || 1;
                const price = item.price || 0;
                return sum + (qty * price);
              }, 0),
              sku: 'ANNULATION',
              // NOTE: acti field will be set by formatOrderData to match restaurant activity code
              preci: [{
                descr: 'commande annulée',
                acti: 'SOB'
              }]
            }
          ],
          sub_total: 0,
          tax_amount: 0,
          total: 0,
          payment_method: { code: 'AUC' },
          note: `Canceled: ${originalTransactionNumber || canceledOrder.posOrderId}`,
          order_channel: session?.orderChannel,
          table_name: session?.tableName,
          guest_count: session?.guests || session?.guestCount,
          session_id: session?.id || session?.sessionId
        },
        note: `Order Canceled: ${canceledOrder.posOrderId}`,
        session_id: session?.id || session?.sessionId,
        user_name: canceledOrder.canceledBy || session?.user?.name,
        auditLog: canceledOrder.auditLog || [],
        printHistory: canceledOrder.printHistory || [],
        printVersionSuffix: canceledOrder.printVersionSuffix,
        isRevised: false,
        replacesCount: 0,
        modImpr: 'FAC',
        storeTaxes: [],
        refs: originalTransactionNumber ? [{
          noTrans: originalTransactionNumber,
          datTrans: formatDateForMEV(originalOrderData?.created || new Date()),
          avantTax: this.formatAmount(originalOrderData?.total || canceledOrder.originalSubtotal || 0)
        }] : []
      };

      const job: OrderJob = {
        id: `cancel_after_print_${Date.now()}`,
        data: cancellationOrderData,
        status: 'pending',
        createdAt: new Date(),
        billType: 'final',
        modImpr: 'FAC'
      };

      console.log('[MEV] Cancellation After Print - Order Data:', JSON.stringify(cancellationOrderData, null, 2));
      console.log('[MEV] Cancellation After Print - Job:', JSON.stringify(job, null, 2));

      this.jobs.push(job);
      await this.processJob(job);

      if (job.status === 'completed' && job.result) {
        console.log('[MEV] Cancellation After Print - Completed successfully');
        console.log('[MEV] Cancellation After Print - Result:', JSON.stringify(job.result, null, 2));
        return job.result;
      }
      if (job.result?.cloudMevOverlay) return job.result;

      throw new Error(job.error || 'Failed to process cancellation after print');
    } catch (error) {
      console.error('[MEV CANCELLATION AFTER PRINT] Error:', error);
      throw error;
    }
  }

  /**
   * Send one occasional third party contract (typTrans TIER).
   * This method does not call formatOrderData.
   */
  public async sendOccasionalThirdParty(input: {
    storeId: string;
    counter: number;
    noTrans?: string;
    userName?: string;
    lines: Array<{ price: number; agreement: string; date: string; payable: string; acti: string }>;
    avantTax: number;
    tps: number;
    tvq: number;
    apresTax: number;
  }): Promise<any> {
    if (!connectionMonitor.getStatus()) {
      throw new Error('A connection is required to send this contract.');
    }
    const storeId = String(input?.storeId || '').trim();
    if (!storeId) throw new Error('Store ID is required.');
    if (!Array.isArray(input?.lines) || input.lines.length === 0) {
      throw new Error('Choose at least one item.');
    }

    const envirn = mevStorage.getEnvirn() || 'DEV';
    const transHeader = JSON.parse(mevStorage.getTransactionHeader() || '{}');
    let companyInfo = JSON.parse(mevStorage.getCompanyInfo() || '{}');
    const status = await getCloudMevStoreStatus(storeId, envirn).catch(() => null);
    const business = status?.snapshot?.business || {};
    if (String(business.Name || '').trim()) {
      companyInfo = {
        Name: String(business.Name || '').trim(),
        email: String(business.email || '').trim(),
        phone: String(business.phone || '').trim(),
        address_1: String(business.address_1 || '').trim(),
        address_2: String(business.address_2 || '').trim(),
        city: String(business.city || '').trim(),
        Province: String(business.Province || '').trim(),
        postcode: String(business.postcode || '').trim(),
        country: String(business.country || '').trim(),
      };
      mevStorage.setCompanyInfo(JSON.stringify(companyInfo));
    }
    const cloud = status?.snapshot?.transHeader || {};
    const csr = status?.snapshot?.csrForm || {};
    const pick = (...values: unknown[]) => String(values.find((value) => String(value || '').trim()) || '');
    const noTPS = pick(transHeader.noTPS, cloud.noTPS, csr.gstNumber);
    const noTVQ = pick(transHeader.noTVQ, cloud.noTVQ, csr.qstNumber);
    const noDossFO = pick(transHeader.noDossFO, cloud.noDossFO, csr.dossierNumber);
    const product = await ensureRqProduct(storeId);
    const nomUtil = String(input.userName || this.getCurrentPosUser().name || 'POS User');

    let docNoCiviq = '';
    if (companyInfo.address_1) {
      const address = String(companyInfo.address_1);
      const patterns = [/\d+-(\d+)[,\s]/, /^(\d+)\s/, /(\d+)/];
      for (const pattern of patterns) {
        const match = address.match(pattern);
        if (match && match[1]) {
          docNoCiviq = match[1];
          break;
        }
      }
    }
    const docCp = String(companyInfo.postcode || '').replace(/\s+/g, '').toUpperCase();

    const now = new Date();
    const datTrans = this.formatDateTime(now);
    const timezone = transHeader.timezone || cloud.timezone || '-05:00';
    const utc = this.isDST(now, timezone);
    const noTrans = String(input.noTrans || input.counter);
    const rqText = (value: string) => String(value || '')
      .replace(/[^a-zA-Z0-9\s\u00C0-\u00FF\u0152\u0153\u0178:'.,-]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 128);

    const transActu: any = {
      sectActi: { abrvt: 'RBC', typServ: 'CMP', nbClint: '001' },
      noTrans,
      nomMandt: companyInfo.Name || '',
      nomUtil,
      ...(docNoCiviq && docCp ? { docAdr: { docNoCiviq, docCp } } : {}),
      relaCommer: 'B2B',
      datTrans,
      utc,
      items: input.lines.map((line) => {
        const prix = this.formatAmount(line.price);
        const acti = line.acti === 'BAR' || line.acti === 'CDR' ? line.acti : 'RES';
        return {
          qte: '+00001.00',
          descr: 'Contrat',
          prix,
          tax: 'FP',
          acti,
          preci: [
            { descr: rqText(line.agreement), acti: 'SOB' },
            { descr: rqText(`ref:${noTrans}`), acti: 'SOB' },
            { descr: rqText(`date:${line.date}`), acti: 'SOB' },
            { descr: rqText(`payable:${line.payable}`), acti: 'SOB' },
          ],
        };
      }),
      mont: {
        avantTax: this.formatAmount(input.avantTax),
        TPS: this.formatAmount(input.tps),
        TVQ: this.formatAmount(input.tvq),
        apresTax: this.formatAmount(input.apresTax),
      },
      noDossFO,
      noTax: { noTPS, noTVQ },
      commerElectr: 'N',
      typTrans: 'TIER',
      modPai: 'SOB',
      modImpr: 'FAC',
      formImpr: 'NON',
      modTrans: 'OPE',
      SEV: {
        idSEV: product.IDSEV,
        idVersi: product.IDVERSI,
        codCertif: product.CODCERTIF,
        idPartn: product.IDPARTN,
        versi: product.VERSI,
        versiParn: product.VERSIPARN,
      },
    };

    const previousSignature = this.getPreviousSignature();
    const signatureData = await this.generateTransactionSignature({
      noTrans,
      datTrans,
      tpsAmount: transActu.mont.TPS,
      tvqAmount: transActu.mont.TVQ,
      apresTax: transActu.mont.apresTax,
      noTPS,
      noTVQ,
      modImpr: 'FAC',
      modTrans: 'OPE',
      preced: previousSignature,
    });
    transActu.signa = signatureData;
    transActu.emprCertifSEV = await mevCertService.getActiveCertThumbprint(storeId, envirn)
      || readThumbprintPointer(storeId, envirn)
      || '';

    const privateKey = await getPrivateKey(storeId, String(envirn).toLowerCase());
    if (!privateKey) throw new Error('Private key not found');
    const authCode = pick(transHeader.authCode, cloud.authCode, csr.authCode);
    const idApprl = pick(transHeader.IDAPPRL, cloud.IDAPPRL, status?.idApprl);
    const signResult = await window.api.signData(`${authCode}${idApprl}${signatureData.actu}`, privateKey);
    if (!signResult?.success || !signResult.signature) throw new Error('Transmission signature failed');
    const rawSignature = String(signResult.signature);
    const SIGNATRANSM = rawSignature.length >= 88 ? rawSignature.slice(0, 88) : rawSignature.padEnd(88, '=');

    const reqTrans = { transActu };
    console.log('[Occasional third party] payload', JSON.stringify(reqTrans));
    let result: any;
    try {
      result = await postPreparedCloudMevSale({
        storeId,
        envirn,
        reqTrans,
        headers: {
          NOTPS: noTPS,
          NOTVQ: noTVQ,
          IDAPPRL: idApprl,
          APPRLINIT: product.APPRLINIT,
          IDSEV: product.IDSEV,
          IDVERSI: product.IDVERSI,
          CODCERTIF: product.CODCERTIF,
          IDPARTN: product.IDPARTN,
          VERSI: product.VERSI,
          VERSIPARN: product.VERSIPARN,
          SIGNATRANSM,
          EMPRCERTIFTRANSM: transActu.emprCertifSEV,
        },
      });
    } catch (error) {
      await logSrmExchange({
        request: reqTrans,
        response: null,
        ok: false,
        failure: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
    await logSrmExchange({
      url: result?.rqUrl,
      request: result?.rqRequest || reqTrans,
      response: result?.rqResponse,
      ok: Boolean(result?.ok),
      status: result?.rqStatus,
      duration: Number(result?.timings?.rqMs || 0),
    });
    if (!result?.ok) {
      mevStorage.setPreviousSignature(previousSignature);
      throw new Error(srmErrorText(result?.rqResponse) || 'The contract was not sent.');
    }
    const acceptedSignature = String(result?.rqRequest?.reqTrans?.transActu?.signa?.actu || '');
    if (acceptedSignature) mevStorage.setPreviousSignature(acceptedSignature);
    return { ...result, reqTrans: result?.rqRequest?.reqTrans || reqTrans, counter: input.counter, noTrans, datTrans };
  }

  /**
   * Send one quotation, estimate, temporary bill, or closing receipt.
   * This method does not call formatOrderData.
   */
  public async sendProposalDocument(input: {
    storeId: string;
    userName?: string;
    noTrans: string;
    typTrans: 'SOUM' | 'ESTM' | 'ADDI' | 'RFER';
    modPai: string;
    formImpr: 'PAP' | 'ELE' | 'PEL';
    items: any[];
    avantTax: number;
    tps: number;
    tvq: number;
    apresTax: number;
    refs?: Array<{ noTrans: string; datTrans: string; avantTax: number }>;
  }): Promise<any> {
    if (!connectionMonitor.getStatus()) {
      throw new Error('A connection is required to send this document.');
    }
    const storeId = String(input?.storeId || '').trim();
    if (!storeId) throw new Error('Store ID is required.');
    if (!Array.isArray(input?.items) || input.items.length === 0) {
      throw new Error('Enter at least one item.');
    }
    const noTrans = String(input.noTrans || '').trim();
    if (!noTrans) throw new Error('A document number is required.');

    const envirn = mevStorage.getEnvirn() || 'DEV';
    const transHeader = JSON.parse(mevStorage.getTransactionHeader() || '{}');
    let companyInfo = JSON.parse(mevStorage.getCompanyInfo() || '{}');
    const status = await getCloudMevStoreStatus(storeId, envirn).catch(() => null);
    const business = status?.snapshot?.business || {};
    if (String(business.Name || '').trim()) {
      companyInfo = {
        Name: String(business.Name || '').trim(),
        email: String(business.email || '').trim(),
        phone: String(business.phone || '').trim(),
        address_1: String(business.address_1 || '').trim(),
        address_2: String(business.address_2 || '').trim(),
        city: String(business.city || '').trim(),
        Province: String(business.Province || '').trim(),
        postcode: String(business.postcode || '').trim(),
        country: String(business.country || '').trim(),
      };
      mevStorage.setCompanyInfo(JSON.stringify(companyInfo));
    }
    const cloud = status?.snapshot?.transHeader || {};
    const csr = status?.snapshot?.csrForm || {};
    const pick = (...values: unknown[]) => String(values.find((value) => String(value || '').trim()) || '');
    const noTPS = pick(transHeader.noTPS, cloud.noTPS, csr.gstNumber);
    const noTVQ = pick(transHeader.noTVQ, cloud.noTVQ, csr.qstNumber);
    const noDossFO = pick(transHeader.noDossFO, cloud.noDossFO, csr.dossierNumber);
    const product = await ensureRqProduct(storeId);
    const nomUtil = String(input.userName || this.getCurrentPosUser().name || 'POS User');

    let docNoCiviq = '';
    if (companyInfo.address_1) {
      const address = String(companyInfo.address_1);
      const patterns = [/\d+-(\d+)[,\s]/, /^(\d+)\s/, /(\d+)/];
      for (const pattern of patterns) {
        const match = address.match(pattern);
        if (match && match[1]) {
          docNoCiviq = match[1];
          break;
        }
      }
    }
    const docCp = String(companyInfo.postcode || '').replace(/\s+/g, '').toUpperCase();
    const now = new Date();
    const datTrans = this.formatDateTime(now);
    const timezone = transHeader.timezone || cloud.timezone || '-05:00';
    const utc = this.isDST(now, timezone);
    const refs = Array.isArray(input.refs) && input.refs.length
      ? input.refs.map((ref) => ({
        noTrans: String(ref.noTrans || ''),
        datTrans: String(ref.datTrans || ''),
        avantTax: this.formatAmount(ref.avantTax),
      })).filter((ref) => ref.noTrans)
      : [];

    const transActu: any = {
      sectActi: { abrvt: 'RBC', typServ: 'CMP', nbClint: '001' },
      noTrans,
      nomMandt: companyInfo.Name || '',
      nomUtil,
      ...(docNoCiviq && docCp ? { docAdr: { docNoCiviq, docCp } } : {}),
      relaCommer: 'B2C',
      datTrans,
      utc,
      items: input.items,
      mont: {
        avantTax: this.formatAmount(input.avantTax),
        TPS: this.formatAmount(input.tps),
        TVQ: this.formatAmount(input.tvq),
        apresTax: this.formatAmount(input.apresTax),
      },
      noDossFO,
      noTax: { noTPS, noTVQ },
      commerElectr: 'N',
      typTrans: input.typTrans,
      modPai: input.modPai,
      modImpr: 'FAC',
      formImpr: input.formImpr,
      modTrans: 'OPE',
      ...(refs.length ? { refs } : {}),
      SEV: {
        idSEV: product.IDSEV,
        idVersi: product.IDVERSI,
        codCertif: product.CODCERTIF,
        idPartn: product.IDPARTN,
        versi: product.VERSI,
        versiParn: product.VERSIPARN,
      },
    };

    const previousSignature = this.getPreviousSignature();
    const signatureData = await this.generateTransactionSignature({
      noTrans,
      datTrans,
      tpsAmount: transActu.mont.TPS,
      tvqAmount: transActu.mont.TVQ,
      apresTax: transActu.mont.apresTax,
      noTPS,
      noTVQ,
      modImpr: 'FAC',
      modTrans: 'OPE',
      preced: String(status?.snapshot?.preced || '').trim() || previousSignature,
    });
    transActu.signa = signatureData;
    transActu.emprCertifSEV = await mevCertService.getActiveCertThumbprint(storeId, envirn)
      || readThumbprintPointer(storeId, envirn)
      || '';

    let qrCodeUrl = '';
    try {
      qrCodeUrl = await this.generateQRCodeUrl({
        ...transActu,
        EMPRCERTIFTRANSM: transActu.emprCertifSEV,
      }, envirn);
    } catch (error) {
      console.error('[Proposal document] QR code was not created', error);
    }

    const privateKey = await getPrivateKey(storeId, String(envirn).toLowerCase());
    if (!privateKey) throw new Error('Private key not found');
    const authCode = pick(transHeader.authCode, cloud.authCode, csr.authCode);
    const idApprl = pick(transHeader.IDAPPRL, cloud.IDAPPRL, status?.idApprl);
    const signResult = await window.api.signData(`${authCode}${idApprl}${signatureData.actu}`, privateKey);
    if (!signResult?.success || !signResult.signature) throw new Error('Transmission signature failed');
    const rawSignature = String(signResult.signature);
    const SIGNATRANSM = rawSignature.length >= 88 ? rawSignature.slice(0, 88) : rawSignature.padEnd(88, '=');

    const reqTrans = { transActu };
    let result: any;
    try {
      result = await postPreparedCloudMevSale({
        storeId,
        envirn,
        reqTrans,
        headers: {
          NOTPS: noTPS,
          NOTVQ: noTVQ,
          IDAPPRL: idApprl,
          APPRLINIT: product.APPRLINIT,
          IDSEV: product.IDSEV,
          IDVERSI: product.IDVERSI,
          CODCERTIF: product.CODCERTIF,
          IDPARTN: product.IDPARTN,
          VERSI: product.VERSI,
          VERSIPARN: product.VERSIPARN,
          SIGNATRANSM,
          EMPRCERTIFTRANSM: transActu.emprCertifSEV,
        },
      });
    } catch (error) {
      await logSrmExchange({
        request: reqTrans,
        response: null,
        ok: false,
        failure: error instanceof Error ? error.message : String(error),
      });
      throw error;
    }
    await logSrmExchange({
      url: result?.rqUrl,
      request: result?.rqRequest || reqTrans,
      response: result?.rqResponse,
      ok: Boolean(result?.ok),
      status: result?.rqStatus,
      duration: Number(result?.timings?.rqMs || 0),
    });
    if (!result?.ok) {
      mevStorage.setPreviousSignature(String(status?.snapshot?.preced || '').trim() || previousSignature);
      throw new Error(srmErrorText(result?.rqResponse) || 'The document was not sent.');
    }
    const acceptedSignature = String(result?.rqRequest?.reqTrans?.transActu?.signa?.actu || '');
    if (acceptedSignature) mevStorage.setPreviousSignature(acceptedSignature);
    return {
      ...result,
      reqTrans: result?.rqRequest?.reqTrans || reqTrans,
      noTrans,
      datTrans,
      qrCodeUrl,
    };
  }

  public getJobs(): OrderJob[] { return this.jobs.slice(-this.MAX_JOBS); }
  public getJobById(jobId: string): OrderJob | undefined { return this.jobs.find(j => j.id === jobId); }
  public emitCloudJson(payload: any): void {
    this.notifyListeners('cloud-json', payload);
  }

  public addEventListener(event: string, callback: any): void {
    if (!this.eventListeners[event]) this.eventListeners[event] = [];
    this.eventListeners[event].push(callback);
  }
  public removeEventListener(event: string, callback: any): void {
    if (this.eventListeners[event]) this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
  }
  private notifyListeners(event: string, data: any): void {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(cb => {
        if (event === 'polling-status-changed') cb(data);
        else cb(data);
      });
    }
  }
}

export default MevOrderJobManager.getInstance();
